import React, { useState, useEffect, useRef } from 'react';
import BinDefinitionModal from './reports/BinDefinitionModal';
import { useSearchParams } from 'react-router-dom';
import {
    Page, Card, Tabs, TextField, Select, Button, ButtonGroup, Badge, Banner,
    BlockStack, InlineStack, Text, Checkbox, Divider, Box, Popover, Modal,
} from '@shopify/polaris';
import { authenticatedFetch, useAuth } from './AuthComponent';
import { WAREHOUSE_V2 } from './useConsoleLocations';
import { STORE_KEYS, STORE_DISPLAY_NAMES } from './StoreContext';
import NotificationsTab from './NotificationsTab';
import SkuToolsTab from './SkuToolsTab';
import TeamPortalTab from './TeamPortalTab';
import CronMonitorTab from './CronMonitorTab';
import LightspeedTab from './LightspeedTab';
import MentionContactsTab from './MentionContactsTab';
import StoreBriefingPromptCard from './StoreBriefingPromptCard';

import { hasRole, hasPermission, userRoles, rolesRequireLocation, roleLabel } from './constants/roles';
const LOCATION_TIMEZONE_OPTIONS = [
    { label: 'Eastern (America/New_York)',  value: 'America/New_York' },
    { label: 'Central (America/Chicago)',   value: 'America/Chicago' },
    { label: 'Mountain (America/Denver)',   value: 'America/Denver' },
    { label: 'Arizona (America/Phoenix)',   value: 'America/Phoenix' },
    { label: 'Pacific (America/Los_Angeles)', value: 'America/Los_Angeles' },
];
const LOCATION_TIMEZONE_LABELS = {
    'America/New_York':    'Eastern',
    'America/Chicago':     'Central',
    'America/Denver':      'Mountain',
    'America/Los_Angeles': 'Pacific',
};

// ── Folder-style tab + bounding box wrapper ──────────────────────────────────
// Renders a strip of buttons where the SELECTED tab visually merges into the
// content bounding box below (file-folder style). Inactive tabs sit outside
// the bounding box. Pass children as the panel content.
//
// `variant`: 'primary' (larger, used for main Settings tabs) or 'sub' (smaller,
// for nested store selectors). Both use the same shape, just different sizing.

function lastUseText(iso) {
    if (!iso) return 'Never';
    const t = new Date(iso).getTime();
    if (isNaN(t)) return 'Never';
    const ms = Date.now() - t;
    if (ms < 0)  return 'Just now';
    const s = Math.floor(ms / 1000);
    if (s < 60)  return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30)  return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
}

function FolderTabsPanel({ tabs, selectedId, onSelect, children, variant = 'sub' }) {
    const className = `settings-folder-tabs settings-folder-tabs--${variant}`;
    return (
        <div className={className}>
            <div className="settings-folder-tabs__strip">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        className={`settings-folder-tabs__tab${t.id === selectedId ? ' settings-folder-tabs__tab--active' : ''}`}
                        onClick={() => onSelect?.(t.id)}
                    >
                        {t.content}
                    </button>
                ))}
            </div>
            <div className="settings-folder-tabs__panel">
                {children}
            </div>
        </div>
    );
}

// Store sub-tabs (wraps FolderTabsPanel with store-specific logic + optional extra tabs).
function StoreSubTabsPanel({ storeKey, onStoreChange, extraTabs = [], selectedExtraId = null, onExtraSelect, children }) {
    const baseTabs = STORE_KEYS.map(sk => ({ id: sk, content: STORE_DISPLAY_NAMES[sk] }));
    const tabs = [...baseTabs, ...extraTabs];
    const selectedId = selectedExtraId || storeKey;
    const handleSelect = (id) => {
        if (STORE_KEYS.includes(id)) onStoreChange?.(id);
        else onExtraSelect?.(extraTabs.find(t => t.id === id));
    };
    return (
        <FolderTabsPanel tabs={tabs} selectedId={selectedId} onSelect={handleSelect} variant="sub">
            {children}
        </FolderTabsPanel>
    );
}

// ── Shared delete confirmation modal ──────────────────────────────────────────

function DeleteConfirmModal({ title, message, onConfirm, onCancel }) {
    return (
        <div className="modal-backdrop">
            <div className="modal">
                <div className="modal__title">{title}</div>
                <div className="modal__body">{message}</div>
                <div className="modal__footer">
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button tone="critical" variant="primary" onClick={onConfirm}>Delete</Button>
                </div>
            </div>
        </div>
    );
}

// Stronger delete confirmation — the caller must type a specific phrase
// (e.g. "DELETE") before the button enables. Use for destructive user-facing
// actions that shouldn't be one-click ("Delete User" from the Edit modal).
function TypeToConfirmModal({ title, message, confirmPhrase = 'DELETE',
                             confirmLabel = 'Delete', onConfirm, onCancel,
                             busy = false }) {
    const [typed, setTyped] = useState('');
    const matches = typed.trim() === confirmPhrase;
    return (
        <div className="modal-backdrop">
            <div className="modal">
                <div className="modal__title">{title}</div>
                <div className="modal__body">
                    <div style={{ marginBottom: 12 }}>{message}</div>
                    <div style={{ marginBottom: 8, fontSize: 13 }}>
                        Type <strong>{confirmPhrase}</strong> to confirm:
                    </div>
                    <input
                        autoFocus
                        type="text"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && matches && !busy) onConfirm(); }}
                        placeholder={confirmPhrase}
                        style={{
                            width: '100%', padding: '8px 12px', fontSize: 14,
                            border: '1px solid #c9cccf', borderRadius: 6,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        }}
                    />
                </div>
                <div className="modal__footer">
                    <Button onClick={onCancel} disabled={busy}>Cancel</Button>
                    <Button
                        tone="critical"
                        variant="primary"
                        onClick={onConfirm}
                        disabled={!matches || busy}
                        loading={busy}
                    >{confirmLabel}</Button>
                </div>
            </div>
        </div>
    );
}

// ── Sortable table header helper ─────────────────────────────────────────────

function SortableHeader({ label, field, sortField, sortDir, onSort, style }) {
    const active = sortField === field;
    return (
        <th
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(style || {}) }}
            onClick={() => onSort(field, active && sortDir === 'asc' ? 'desc' : 'asc')}
        >
            {label} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>▲</span>}
        </th>
    );
}

function useSortableTable(defaultField = null, defaultDir = 'asc') {
    const [sortField, setSortField] = useState(defaultField);
    const [sortDir, setSortDir]     = useState(defaultDir);
    const onSort = (field, dir) => { setSortField(field); setSortDir(dir); };
    const sortRows = (rows, accessor) => {
        if (!sortField) return rows;
        const sorted = [...rows].sort((a, b) => {
            const av = (accessor(a, sortField) ?? '').toString().toLowerCase();
            const bv = (accessor(b, sortField) ?? '').toString().toLowerCase();
            return av < bv ? -1 : av > bv ? 1 : 0;
        });
        return sortDir === 'desc' ? sorted.reverse() : sorted;
    };
    return { sortField, sortDir, onSort, sortRows };
}

// ── Locations tab ─────────────────────────────────────────────────────────────

// Per-store logo used at the top of the order printout (both bulk + individual,
// since both share OrderPrintCard). Loads metadata on mount, lets admin upload/
// replace/remove. Pre-population for every store happens at backend startup
// (pulled from Shopify checkout branding) — this UI just lets users override.
function StoreBrandingCard({ storeKey, displayName }) {
    const [meta, setMeta] = useState(null);    // { has_logo, url, logo_uploaded_at, logo_uploaded_by, logo_source }
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const fileInputRef = useRef(null);

    const load = () => {
        setLoading(true);
        authenticatedFetch(`/api/store/logo?store=${storeKey}`)
            .then(r => r.json())
            .then(d => setMeta(d))
            .finally(() => setLoading(false));
    };
    useEffect(load, [storeKey]); // eslint-disable-line

    const onPickFile = () => fileInputRef.current?.click();

    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';                                // reset so picking the same file again re-triggers
        if (!file) return;
        if (!file.type.includes('png')) {
            setError('Logo must be a PNG image.'); setSuccess(null); return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setError('Logo must be 2 MB or smaller.'); setSuccess(null); return;
        }
        setUploading(true); setError(null); setSuccess(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await authenticatedFetch(`/api/store/logo?store=${storeKey}`, { method: 'POST', body: fd });
            const data = await res.json();
            if (!res.ok) {
                setError(data.detail || 'Upload failed.');
            } else {
                setSuccess('Logo updated.');
                load();
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setUploading(false);
        }
    };

    const onRemove = async () => {
        if (!window.confirm(`Remove the logo for ${displayName}? Order printouts will fall back to the store name in plain text until a new logo is uploaded.`)) return;
        setError(null); setSuccess(null);
        const res = await authenticatedFetch(`/api/store/logo?store=${storeKey}`, { method: 'DELETE' });
        if (!res.ok) {
            const d = await res.json().catch(() => ({}));
            setError(d.detail || 'Remove failed.');
        } else {
            setSuccess('Logo removed.');
            load();
        }
    };

    const formattedDate = meta?.logo_uploaded_at
        ? new Date(meta.logo_uploaded_at).toLocaleString()
        : null;
    const sourceLabel = {
        upload:                      'Uploaded manually',
        shopify_checkout_branding:   'Pulled from Shopify checkout branding',
        public_asset:                'Bundled default',
    }[meta?.logo_source] || meta?.logo_source;

    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h3">Store branding</Text>
                    <Text tone="subdued" as="span">Used at the top of printed orders + pick lists</Text>
                </InlineStack>

                {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                {success && <Banner tone="success" onDismiss={() => setSuccess(null)}>{success}</Banner>}

                <InlineStack gap="400" blockAlign="center" wrap={false}>
                    {/* Preview pane — matches the print template proportions */}
                    <Box
                        padding="300"
                        background="bg-surface-secondary"
                        borderWidth="025"
                        borderColor="border"
                        borderRadius="200"
                        minWidth="220px"
                    >
                        {loading ? (
                            <Text tone="subdued">Loading…</Text>
                        ) : meta?.has_logo ? (
                            <img
                                src={meta.url}
                                alt={`${displayName} logo`}
                                style={{ maxHeight: 80, maxWidth: 220, display: 'block' }}
                            />
                        ) : (
                            <Text variant="headingMd" as="div" tone="subdued">{displayName}</Text>
                        )}
                    </Box>

                    {/* Controls */}
                    <BlockStack gap="200">
                        <ButtonGroup>
                            <Button
                                variant="primary"
                                loading={uploading}
                                disabled={loading}
                                onClick={onPickFile}
                            >
                                {meta?.has_logo ? 'Replace logo' : 'Upload logo'}
                            </Button>
                            {meta?.has_logo && (
                                <Button tone="critical" onClick={onRemove} disabled={uploading}>
                                    Remove
                                </Button>
                            )}
                        </ButtonGroup>
                        <Text tone="subdued" as="span">PNG only · max 2 MB · max 2000×2000 px</Text>
                        {meta?.has_logo && formattedDate && (
                            <Text tone="subdued" as="span">
                                {sourceLabel}
                                {meta.logo_uploaded_by ? ` by ${meta.logo_uploaded_by}` : ''} · {formattedDate}
                            </Text>
                        )}
                        {!meta?.has_logo && !loading && (
                            <Text tone="subdued" as="span">
                                No logo set — printouts show the store name in plain text.
                            </Text>
                        )}
                    </BlockStack>
                </InlineStack>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png"
                    onChange={onFileChange}
                    style={{ display: 'none' }}
                />
            </BlockStack>
        </Card>
    );
}

// HTML footer rendered at the bottom of order printouts (both bulk + individual).
// Saved to operations_store_settings.settings.printout_footer_html. Backend
// allowlist-sanitizes on save so we can render with dangerouslySetInnerHTML on
// the print template without an XSS risk from a compromised admin.
function StorePrintoutFooterCard({ storeKey, displayName }) {
    const [html, setHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const load = () => {
        setLoading(true);
        authenticatedFetch(`/api/store/printout-footer?store=${storeKey}`)
            .then(r => r.json())
            .then(d => { setHtml(d.html || ''); setDirty(false); })
            .finally(() => setLoading(false));
    };
    useEffect(load, [storeKey]); // eslint-disable-line

    const onSave = async () => {
        setSaving(true); setError(null); setSuccess(null);
        try {
            const res = await authenticatedFetch(`/api/store/printout-footer?store=${storeKey}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.detail || 'Save failed.');
            } else {
                setHtml(data.html || '');
                setDirty(false);
                setSuccess('Printout footer saved.');
            }
        } catch (err) { setError(String(err)); }
        finally { setSaving(false); }
    };

    const onResetToDefault = async () => {
        if (!window.confirm(`Reset the printout footer for ${displayName} to the default ("Thank you for choosing ${displayName} / We hope you love your new gear. Play Local. Shop Local.")?`)) return;
        setSaving(true); setError(null); setSuccess(null);
        try {
            const res = await authenticatedFetch(`/api/store/printout-footer?store=${storeKey}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html: '' }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.detail || 'Reset failed.');
            } else {
                setHtml(data.html || '');
                setDirty(false);
                setSuccess('Reverted to default.');
            }
        } catch (err) { setError(String(err)); }
        finally { setSaving(false); }
    };

    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h3">Printout footer</Text>
                    <Text tone="subdued" as="span">Rendered at the bottom of every order printout (HTML)</Text>
                </InlineStack>

                {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
                {success && <Banner tone="success" onDismiss={() => setSuccess(null)}>{success}</Banner>}

                <InlineStack gap="400" wrap={false} blockAlign="start">
                    {/* Editor */}
                    <Box minWidth="50%">
                        <TextField
                            label="HTML"
                            value={html}
                            multiline={6}
                            autoComplete="off"
                            onChange={(v) => { setHtml(v); setDirty(true); }}
                            disabled={loading || saving}
                            helpText='Allowed tags: div, p, span, br, strong/em/b/i/u, a (with href), h1–h6, ul/ol/li. Use class="sp-doc-footer__title" for the bold title style and class="sp-doc-footer__sub" for the small grey subtitle.'
                        />
                        <Box paddingBlockStart="200">
                            <ButtonGroup>
                                <Button
                                    variant="primary"
                                    onClick={onSave}
                                    loading={saving}
                                    disabled={!dirty || loading}
                                >
                                    Save
                                </Button>
                                <Button onClick={onResetToDefault} disabled={loading || saving} tone="critical">
                                    Reset to default
                                </Button>
                            </ButtonGroup>
                        </Box>
                    </Box>

                    {/* Preview — rendered in a sandboxed iframe so any markup
                        the admin types (even an accidental <script>) can't
                        execute against the Settings page. Final stored value
                        is also allowlist-sanitized on the backend on save. */}
                    <Box
                        padding="400"
                        background="bg-surface-secondary"
                        borderWidth="025"
                        borderColor="border"
                        borderRadius="200"
                        minWidth="40%"
                    >
                        <Text tone="subdued" as="span">Preview</Text>
                        <iframe
                            title="Printout footer preview"
                            sandbox=""
                            style={{ width: '100%', height: 100, border: 'none', marginTop: 8, background: '#fff' }}
                            srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
                                body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 12px; line-height: 1.45; color: #111418; }
                                .sp-doc-footer { margin-top: 12px; padding-top: 12px; border-top: 1px solid #d0d5dd; text-align: center; }
                                .sp-doc-footer__title { font-size: 14px; font-weight: 700; color: #111418; }
                                .sp-doc-footer__sub { font-size: 11px; color: #6d7175; margin-top: 2px; }
                            </style></head><body><div class="sp-doc-footer">${html || '<span style="color:#9ca3af">(empty)</span>'}</div></body></html>`}
                        />
                    </Box>
                </InlineStack>
            </BlockStack>
        </Card>
    );
}

function LocationsTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [locations, setLocations]     = useState([]);
    const [shopifyLocs, setShopifyLocs] = useState([]);
    const [showAdd, setShowAdd]         = useState(false);
    const [form, setForm]               = useState({ name: '', shopify_location_id: '', timezone: 'America/New_York' });
    const [editId, setEditId]           = useState(null);
    const [editForm, setEditForm]       = useState({});
    const [loading, setLoading]         = useState(true);
    const [msg, setMsg]                 = useState(null);
    const [msgError, setMsgError]       = useState(false);
    const [syncStatus, setSyncStatus]   = useState({}); // loc_id → {status, pages, orders}
    const syncPollers                   = React.useRef({});
    const [backfillStatus, setBackfillStatus] = useState({}); // loc_id → {status, done, total}
    const backfillPollers               = React.useRef({});
    const urlStore = searchParams.get('store');
    const urlLocId = parseInt(searchParams.get('loc') || '', 10) || null;
    const [storeKey, setStoreKey]       = useState(
        urlStore && STORE_KEYS.includes(urlStore) ? urlStore : STORE_KEYS[0]
    );

    // Keep storeKey state in sync if URL changes externally
    useEffect(() => {
        if (urlStore && STORE_KEYS.includes(urlStore) && urlStore !== storeKey) {
            setStoreKey(urlStore);
        }
    }, [urlStore]); // eslint-disable-line

    const setStoreAndUrl = (sk) => {
        setStoreKey(sk);
        setShowAdd(false);
        setEditId(null);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'locations');
            next.set('store', sk);
            next.delete('loc');
            return next;
        }, { replace: true });
    };

    const openLocation = (loc) => {
        setEditId(null);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'locations');
            next.set('store', storeKey);
            next.set('loc', String(loc.id));
            return next;
        });
    };

    const clearLocation = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('loc');
            return next;
        });
    };

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const load = () => {
        setLoading(true);
        authenticatedFetch(`/api/locations?store=${storeKey}`).then(r => r.json()).then(setLocations).finally(() => setLoading(false));
    };

    useEffect(() => {
        setLocations([]);
        setShopifyLocs([]);
        load();
    }, [storeKey]); // eslint-disable-line

    // Clean up pollers on unmount
    useEffect(() => {
        return () => Object.values(syncPollers.current).forEach(clearInterval);
    }, []);

    const pollSyncStatus = (locId) => {
        if (syncPollers.current[locId]) return; // already polling
        syncPollers.current[locId] = setInterval(async () => {
            const res = await authenticatedFetch(`/api/locations/${locId}/sync-status`);
            if (!res.ok) return;
            const s = await res.json();
            setSyncStatus(prev => ({ ...prev, [locId]: s }));
            if (s.status !== 'running') {
                clearInterval(syncPollers.current[locId]);
                delete syncPollers.current[locId];
                if (s.status === 'done') flash(`Sync complete — ${s.orders} orders cached.`);
                if (s.status === 'error') flash(`Sync error: ${s.error}`, true);
            }
        }, 2000);
    };

    const handleSync = async (loc) => {
        const res = await authenticatedFetch(`/api/locations/${loc.id}/sync?store=${storeKey}`, { method: 'POST' });
        const data = await res.json();
        if (data.started) {
            setSyncStatus(prev => ({ ...prev, [loc.id]: { status: 'running', pages: 0, orders: 0 } }));
            pollSyncStatus(loc.id);
        } else {
            flash(data.message);
        }
    };

    const pollBackfillStatus = (locId) => {
        if (backfillPollers.current[locId]) return;
        backfillPollers.current[locId] = setInterval(async () => {
            const res = await authenticatedFetch(`/api/locations/${locId}/backfill_ops_history?store=${storeKey}`);
            if (!res.ok) return;
            const s = await res.json();
            setBackfillStatus(prev => ({ ...prev, [locId]: s }));
            if (s.status !== 'running') {
                clearInterval(backfillPollers.current[locId]);
                delete backfillPollers.current[locId];
                if (s.status === 'done') flash(`Backfill complete — ${s.done}/${s.total} orders processed.`);
                if (s.status === 'error') flash(`Backfill error: ${s.error}`, true);
            }
        }, 3000);
    };

    const handleBackfill = async (loc) => {
        const res = await authenticatedFetch(`/api/locations/${loc.id}/backfill_ops_history?store=${storeKey}`, { method: 'POST' });
        const data = await res.json();
        if (data.started) {
            setBackfillStatus(prev => ({ ...prev, [loc.id]: { status: 'running', done: 0, total: 0 } }));
            pollBackfillStatus(loc.id);
            flash('Backfill started — this may take a while.');
        } else {
            flash(data.message || data.detail || 'Could not start backfill');
        }
    };

    // On mount / when opening a detail view, refresh backfill status from server
    // so the button reflects the true state even after a page reload.
    useEffect(() => {
        return () => Object.values(backfillPollers.current).forEach(clearInterval);
    }, []);

    const loadShopifyLocs = () => {
        authenticatedFetch(`/api/shopify/locations?store=${storeKey}`).then(r => r.json()).then(setShopifyLocs).catch(() => {});
    };

    const notifyLocationsChanged = () => window.dispatchEvent(new CustomEvent('locations-changed'));

    const handleAdd = async () => {
        const res = await authenticatedFetch(`/api/locations?store=${storeKey}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
        if (res.ok) {
            const loc = await res.json();
            flash('Location added — syncing orders in background…');
            setShowAdd(false);
            setForm({ name: '', shopify_location_id: '', timezone: 'America/New_York' });
            load();
            notifyLocationsChanged();
            setSyncStatus(prev => ({ ...prev, [loc.id]: { status: 'running', pages: 0, orders: 0 } }));
            pollSyncStatus(loc.id);
        } else { const e = await res.json(); flash(`Error: ${e.detail}`, true); }
    };

    const handleUpdate = async (id) => {
        const res = await authenticatedFetch(`/api/locations/${id}?store=${storeKey}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editForm) });
        if (res.ok) { flash('Location updated.'); setEditId(null); load(); notifyLocationsChanged(); }
        else { const e = await res.json(); flash(`Error: ${e.detail}`, true); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this location?')) return;
        await authenticatedFetch(`/api/locations/${id}?store=${storeKey}`, { method: 'DELETE' });
        load(); notifyLocationsChanged();
    };

    const selectedLoc = urlLocId ? locations.find(l => l.id === urlLocId) : null;
    const extraTabs = selectedLoc ? [{
        id: `loc-${selectedLoc.id}`,
        content: `📍 ${selectedLoc.name}`,
        panelID: `loc-${selectedLoc.id}-panel`,
    }] : [];

    return (
        <StoreSubTabsPanel
            storeKey={storeKey}
            onStoreChange={setStoreAndUrl}
            extraTabs={extraTabs}
            selectedExtraId={selectedLoc ? `loc-${selectedLoc.id}` : null}
            onExtraSelect={() => { /* already here */ }}
        >
        {selectedLoc ? (
            <LocationDetailView
                loc={selectedLoc}
                storeKey={storeKey}
                syncStatus={syncStatus[selectedLoc.id]}
                backfillStatus={backfillStatus[selectedLoc.id]}
                onBack={clearLocation}
                onSync={() => handleSync(selectedLoc)}
                onBackfill={() => handleBackfill(selectedLoc)}
                onEdit={() => { setEditId(selectedLoc.id); setEditForm({ name: selectedLoc.name, shopify_location_id: selectedLoc.shopify_location_id, active: selectedLoc.active, is_franchise: selectedLoc.is_franchise || false, is_store: selectedLoc.is_store || false, timezone: selectedLoc.timezone || 'America/New_York' }); }}
                onDelete={() => handleDelete(selectedLoc.id)}
                editMode={editId === selectedLoc.id}
                editForm={editForm}
                setEditForm={setEditForm}
                onSave={() => handleUpdate(selectedLoc.id)}
                onCancelEdit={() => setEditId(null)}
            />
        ) : (
        <BlockStack gap="400">
            {msg && (
                <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>
            )}

            {/* Store branding — logo used at the top of order printouts */}
            <StoreBrandingCard
                storeKey={storeKey}
                displayName={STORE_DISPLAY_NAMES?.[storeKey] || storeKey}
            />

            {/* Printout footer — HTML at the bottom of order printouts */}
            <StorePrintoutFooterCard
                storeKey={storeKey}
                displayName={STORE_DISPLAY_NAMES?.[storeKey] || storeKey}
            />

            {/* Import from Shopify */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingSm" as="h3">Import from Shopify</Text>
                    <Box>
                        <Button onClick={loadShopifyLocs}>Fetch Shopify Locations</Button>
                    </Box>
                    {shopifyLocs.length > 0 && (() => {
                        // Build a Set of shopify_location_ids already in our DB
                        // so the fetched Shopify list can highlight duplicates
                        // and swap the "Add" action for an "Added" badge.
                        const addedSlocIds = new Set(
                          (locations || [])
                            .map(l => String(l.shopify_location_id || ''))
                            .filter(Boolean)
                        );
                        const newCount = shopifyLocs.filter(sl => !addedSlocIds.has(String(sl.id))).length;
                        return (
                            <div>
                                <Text tone="subdued" as="p">
                                    {shopifyLocs.length} locations found · {newCount} not yet in OpsHub
                                </Text>
                                <div className="settings-loc-table-wrap">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                {['ID', 'Name', 'City', ''].map(h => (
                                                    <th key={h}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                    </table>
                                    <div className="settings-loc-table-scroll">
                                        <table className="data-table">
                                            <tbody>
                                                {shopifyLocs.map(sl => {
                                                    const isAdded = addedSlocIds.has(String(sl.id));
                                                    return (
                                                        <tr key={sl.id}
                                                            style={isAdded ? { background: '#d1fae5' } : undefined}>
                                                            <td className="td-shopify-id">{sl.id}</td>
                                                            <td>{sl.name}</td>
                                                            <td className="td-muted">{sl.city}</td>
                                                            <td>
                                                                {isAdded ? (
                                                                    <Text tone="success" as="span" fontWeight="semibold">✓ Added</Text>
                                                                ) : (
                                                                    <Button size="slim" onClick={() => { setForm({ name: sl.name, shopify_location_id: String(sl.id), timezone: 'America/New_York' }); setShowAdd(true); }}>
                                                                        Add
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </BlockStack>
            </Card>

            {/* Locations list */}
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingSm" as="h3">Locations</Text>
                        {!showAdd && <Button variant="primary" onClick={() => setShowAdd(true)}>Add Location</Button>}
                    </InlineStack>

                    {loading ? (
                        <Text tone="subdued">Loading…</Text>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    {['Name', 'Shopify Location ID', 'Time Zone', 'Status', 'Franchise', 'Store', 'Actions'].map(h => (
                                        <th key={h}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {locations.map(loc => (
                                    <tr key={loc.id}>
                                        {editId === loc.id ? (
                                            <>
                                                <td>
                                                    <TextField label="" labelHidden value={editForm.name || ''} onChange={v => setEditForm(f => ({ ...f, name: v }))} autoComplete="off" />
                                                </td>
                                                <td>
                                                    <TextField label="" labelHidden value={editForm.shopify_location_id || ''} onChange={v => setEditForm(f => ({ ...f, shopify_location_id: v }))} autoComplete="off" />
                                                </td>
                                                <td>
                                                    <Select label="" labelHidden options={LOCATION_TIMEZONE_OPTIONS} value={editForm.timezone || 'America/New_York'} onChange={v => setEditForm(f => ({ ...f, timezone: v }))} />
                                                </td>
                                                <td>
                                                    <Checkbox label="Active" checked={!!editForm.active} onChange={v => setEditForm(f => ({ ...f, active: v }))} />
                                                </td>
                                                <td>
                                                    <Checkbox label="Franchise" checked={!!editForm.is_franchise} onChange={v => setEditForm(f => ({ ...f, is_franchise: v }))} />
                                                </td>
                                                <td>
                                                    <BlockStack gap="100">
                                                        <Checkbox label="Store" checked={!!editForm.is_store} onChange={v => setEditForm(f => ({ ...f, is_store: v }))} />
                                                        {editForm.is_store && !/^Soccer Post\b/i.test(editForm.name || '') && (
                                                            <Text tone="caution" variant="bodyXs">Stores usually start with "Soccer Post"</Text>
                                                        )}
                                                    </BlockStack>
                                                </td>
                                                <td>
                                                    <InlineStack gap="200">
                                                        <Button variant="primary" size="slim" onClick={() => handleUpdate(loc.id)}>Save</Button>
                                                        <Button size="slim" onClick={() => setEditId(null)}>Cancel</Button>
                                                    </InlineStack>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="td-bold">
                                                    <button
                                                        type="button"
                                                        onClick={() => openLocation(loc)}
                                                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--p-color-text-link)', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                                                    >
                                                        {loc.name}
                                                    </button>
                                                </td>
                                                <td className="td-shopify-id">{loc.shopify_location_id}</td>
                                                <td>{LOCATION_TIMEZONE_LABELS[loc.timezone] || loc.timezone || 'Eastern'}</td>
                                                <td>
                                                    <Badge tone={loc.active ? 'success' : undefined}>{loc.active ? 'Active' : 'Inactive'}</Badge>
                                                </td>
                                                <td>
                                                    {loc.is_franchise && <Badge tone="info">Franchise</Badge>}
                                                </td>
                                                <td>
                                                    {loc.is_store && <Badge tone="attention">Store</Badge>}
                                                </td>
                                                <td>
                                                    <InlineStack gap="200" blockAlign="center">
                                                        <Button size="slim" onClick={() => openLocation(loc)}>Open</Button>
                                                        <Button size="slim" onClick={() => { setEditId(loc.id); setEditForm({ name: loc.name, shopify_location_id: loc.shopify_location_id, active: loc.active, is_franchise: loc.is_franchise || false, is_store: loc.is_store || false, timezone: loc.timezone || 'America/New_York' }); }}>
                                                            Edit
                                                        </Button>
                                                        {(() => {
                                                            const s = syncStatus[loc.id];
                                                            if (s?.status === 'running') {
                                                                return <span className="settings-sync-status">⟳ Syncing… {s.orders} orders</span>;
                                                            }
                                                            return (
                                                                <Button size="slim" onClick={() => handleSync(loc)}>
                                                                    ↻ Sync Orders
                                                                </Button>
                                                            );
                                                        })()}
                                                        <Button size="slim" tone="critical" onClick={() => handleDelete(loc.id)}>Remove</Button>
                                                    </InlineStack>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {showAdd && (
                        <>
                            <Divider />
                            <BlockStack gap="300">
                                <Text variant="headingSm" as="h3">Add Location</Text>
                                <TextField label="Location Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Soccer Post Rochester" autoComplete="off" />
                                <TextField label="Shopify Location ID" value={form.shopify_location_id} onChange={v => setForm(f => ({ ...f, shopify_location_id: v }))} placeholder="75226874034" autoComplete="off" />
                                <Select label="Time Zone" options={LOCATION_TIMEZONE_OPTIONS} value={form.timezone || 'America/New_York'} onChange={v => setForm(f => ({ ...f, timezone: v }))} helpText="Used for per-location report bucketing and date-filter interpretation." />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={form.is_franchise || false} onChange={e => setForm(f => ({ ...f, is_franchise: e.target.checked }))} />
                                    Franchise location
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={form.is_store || false} onChange={e => setForm(f => ({ ...f, is_store: e.target.checked }))} />
                                    Store location <span style={{ color: '#6b7280', fontSize: '12px' }}>— retail / pickup store; orders are always ingested</span>
                                </label>
                                {form.is_store && form.name && !/^Soccer Post\b/i.test(form.name) && (
                                    <Text tone="caution" variant="bodySm">Stores typically start with "Soccer Post"</Text>
                                )}
                                <InlineStack gap="200">
                                    <Button variant="primary" onClick={handleAdd}>Add Location</Button>
                                    <Button onClick={() => setShowAdd(false)}>Cancel</Button>
                                </InlineStack>
                            </BlockStack>
                        </>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
        )}
        </StoreSubTabsPanel>
    );
}

// ── Location detail view (single-location focused edit page) ─────────────────

function LocationDetailView({ loc, storeKey, syncStatus, backfillStatus, onBack, onSync, onBackfill, onEdit, onDelete, editMode, editForm, setEditForm, onSave, onCancelEdit }) {
    return (
        <BlockStack gap="400">
            <InlineStack gap="200" blockAlign="center">
                <Button onClick={onBack}>← All locations</Button>
                <Text tone="subdued">in {STORE_DISPLAY_NAMES[storeKey]}</Text>
            </InlineStack>
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingLg" as="h2">{loc.name}</Text>
                        <InlineStack gap="200">
                            {loc.is_franchise && <Badge tone="info">Franchise</Badge>}
                            {loc.is_store && <Badge tone="attention">Store</Badge>}
                            <Badge tone={loc.active ? 'success' : undefined}>{loc.active ? 'Active' : 'Inactive'}</Badge>
                        </InlineStack>
                    </InlineStack>
                    <Divider />
                    {editMode ? (
                        <BlockStack gap="300">
                            <TextField label="Location Name" value={editForm.name || ''} onChange={v => setEditForm(f => ({ ...f, name: v }))} autoComplete="off" />
                            <TextField label="Shopify Location ID" value={editForm.shopify_location_id || ''} onChange={v => setEditForm(f => ({ ...f, shopify_location_id: v }))} autoComplete="off" />
                            <Select label="Time Zone" options={LOCATION_TIMEZONE_OPTIONS} value={editForm.timezone || 'America/New_York'} onChange={v => setEditForm(f => ({ ...f, timezone: v }))} />
                            <Checkbox label="Active" checked={!!editForm.active} onChange={v => setEditForm(f => ({ ...f, active: v }))} />
                            <Checkbox label="Franchise location" checked={!!editForm.is_franchise} onChange={v => setEditForm(f => ({ ...f, is_franchise: v }))} />
                            <Checkbox label="Store location — retail / pickup store; orders are always ingested" checked={!!editForm.is_store} onChange={v => setEditForm(f => ({ ...f, is_store: v }))} />
                            {editForm.is_store && !/^Soccer Post\b/i.test(editForm.name || '') && (
                                <Text tone="caution" variant="bodySm">Stores typically start with "Soccer Post"</Text>
                            )}
                            <InlineStack gap="200">
                                <Button variant="primary" onClick={onSave}>Save</Button>
                                <Button onClick={onCancelEdit}>Cancel</Button>
                            </InlineStack>
                        </BlockStack>
                    ) : (
                        <BlockStack gap="300">
                            <DetailRow label="Shopify Location ID" value={loc.shopify_location_id} />
                            <DetailRow label="Time Zone" value={LOCATION_TIMEZONE_LABELS[loc.timezone] || loc.timezone || 'Eastern'} />
                            <DetailRow label="Store" value={STORE_DISPLAY_NAMES[storeKey]} />
                            <InlineStack gap="200">
                                <Button variant="primary" onClick={onEdit}>Edit</Button>
                                {syncStatus?.status === 'running'
                                    ? <Text tone="subdued">⟳ Syncing… {syncStatus.orders} orders</Text>
                                    : <Button onClick={onSync}>↻ Sync Orders</Button>}
                                {loc.is_store && (
                                    backfillStatus?.status === 'running'
                                        ? <Text tone="subdued">⟳ Backfilling… {backfillStatus.done}/{backfillStatus.total}</Text>
                                        : <Button onClick={onBackfill}>↻ Backfill Orders From History</Button>
                                )}
                                <Button tone="critical" onClick={onDelete}>Remove</Button>
                            </InlineStack>
                            {loc.is_store && (
                                <Text tone="subdued" variant="bodySm">
                                    Backfill scans every historical order transferred to this location (or with pickup_store_name matching this store) and populates cache rows so they appear in Orders + portals. Safe to run repeatedly; idempotent.
                                </Text>
                            )}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

function DetailRow({ label, value }) {
    return (
        <InlineStack gap="400" blockAlign="center">
            <div style={{ minWidth: 180 }}>
                <Text tone="subdued">{label}</Text>
            </div>
            <Text>{value}</Text>
        </InlineStack>
    );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

// Drop pins for roles no longer held and for locations no longer assigned.
const LOCATION_LINES_FOLD = 6;
function LocationLines({ rows }) {
    const [open, setOpen] = React.useState(false);
    const shown = open || rows.length <= LOCATION_LINES_FOLD + 1 ? rows : rows.slice(0, LOCATION_LINES_FOLD);
    return (
        <div>
            {shown.map((r, i) => (
                <div key={i} style={r.location_id < 0 ? { fontWeight: 600 } : undefined}>{r.prefix}: {r.name}</div>
            ))}
            {shown.length < rows.length && (
                <Button variant="plain" size="slim" onClick={() => setOpen(true)}>{`+${rows.length - shown.length} more`}</Button>
            )}
            {open && rows.length > LOCATION_LINES_FOLD + 1 && (
                <Button variant="plain" size="slim" onClick={() => setOpen(false)}>Show less</Button>
            )}
        </div>
    );
}
function _cleanPins(pins, roles, assignments) {
    const out = {};
    const keep = new Set((assignments || []).map(a => `${a.store_key}:${a.location_id}`));
    for (const [r, locs] of Object.entries(pins || {})) {
        if (!(roles || []).includes(r)) continue;
        const l = (locs || []).filter(a => keep.has(`${a.store_key}:${a.location_id}`));
        if (l.length) out[r] = l;
    }
    return out;
}
const EMPTY_USER_FORM = { username: '', password: '', first_name: '', last_name: '', email: '', role: 'user', roles: ['user'], role_locations: {}, assignments: [], permissions: [] };
// Roles that can be pinned to some of the account's locations (the others apply everywhere).
const SCOPED_ROLES = ['user', 'franchise', 'store_manager', 'site_manager', 'crm', 'external'];
// Location groups (2026-10-15, Eric): Soccer Post is the only store with
// corporate stores, franchise stores, Teams locations and warehouses, so an
// account can be given a whole group at once. A group is an assignment with a
// NEGATIVE location_id; the backend expands it on every request, so a store
// that opens next month is covered without editing the user. Mirrors
// database.LOCATION_GROUPS.
const SP_LOCATION_GROUPS = [
    { id: -1, name: 'All Corporate Stores', group: 'corporate' },
    { id: -2, name: 'All Teams Locations', group: 'teams' },
    { id: -3, name: 'All Warehouses', group: 'warehouse' },
    { id: -4, name: 'All Franchise Stores', group: 'franchise' },
];
function spLocGroup(loc) {
    if (loc.is_franchise) return 'franchise';
    if (loc.is_store) return 'corporate';
    return /^Teams\b/i.test(loc.name || '') ? 'teams' : 'warehouse';
}
function locationGroupFor(a) {
    return a && a.store_key === 'soccerpost' && a.location_id < 0
        ? (SP_LOCATION_GROUPS.find(g => g.id === a.location_id) || null) : null;
}
// Direct assignments plus every current member of each assigned group, deduped.
function expandAssignments(assignments, allLocationsByStore) {
    const out = [], seen = new Set();
    const push = (sk, id) => { const k = `${sk}:${id}`; if (!seen.has(k)) { seen.add(k); out.push({ store_key: sk, location_id: id }); } };
    for (const a of (assignments || [])) {
        const g = locationGroupFor(a);
        if (!g) { push(a.store_key, a.location_id); continue; }
        for (const l of ((allLocationsByStore || {}).soccerpost || [])) if (spLocGroup(l) === g.group) push('soccerpost', l.id);
    }
    return out;
}
const LOCATION_REQUIRED_MSG = 'Users with the "user", "crm" or "external" role must be assigned at least one location.';

const PASSWORD_RULES_HINT = 'At least 8 characters, one uppercase letter, and one number or symbol.';
function validatePasswordComplexity(pw) {
    if (!pw || pw.length < 8) return PASSWORD_RULES_HINT;
    if (!/[A-Z]/.test(pw)) return PASSWORD_RULES_HINT;
    if (!/[\d\W_]/.test(pw)) return PASSWORD_RULES_HINT;
    return null;
}

function LocationMultiSelect({ locations, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const toggle = (id) => {
        onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
    };
    const label = selected.length === 0
        ? 'No locations'
        : selected.length === 1
            ? (locations.find(l => l.id === selected[0])?.name ?? '1 location')
            : `${selected.length} locations`;

    const activator = (
        <button onClick={() => setOpen(v => !v)} className="loc-select-btn">
            <span>{label}</span>
            <span className="loc-select-btn__chevron">▾</span>
        </button>
    );

    return (
        <Popover active={open} activator={activator} onClose={() => setOpen(false)} preferredAlignment="left">
            <div className="loc-dropdown">
                {selected.length > 0 && (
                    <div className="loc-dropdown__clear">
                        <button onClick={() => onChange([])} className="loc-dropdown__clear-btn">
                            Clear selection
                        </button>
                    </div>
                )}
                {locations.map(l => (
                    <label key={l.id} className={`loc-dropdown__option${selected.includes(l.id) ? ' loc-dropdown__option--selected' : ''}`}>
                        <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggle(l.id)} />
                        <span className="loc-dropdown__option-text">{l.name}</span>
                    </label>
                ))}
                {locations.length === 0 && <div className="loc-dropdown__empty">No locations</div>}
            </div>
        </Popover>
    );
}

function UsersTab() {
    const [users, setUsers]             = useState([]);
    const [allLocationsByStore, setLocs] = useState({});
    const [showAdd, setShowAdd]         = useState(false);
    const [form, setForm]               = useState(EMPTY_USER_FORM);
    // Duplicate (Eric, 2026-10-20): like the PIN-user button — the Add User modal opens pre-filled with
    // the source account's roles, role pins, locations and extra permissions; name/email/username blank.
    const [duplicateFromName, setDuplicateFromName] = useState('');
    const [nameFilter, setNameFilter]   = useState('');
    const [roleFilter, setRoleFilter]   = useState('all');
    const [locFilter, setLocFilter]     = useState('all'); // 'all' | 'none' | '<store_key>:<location_id>'
    // Modal-scoped error so validation messages render inside the Add User
    // modal instead of behind it on the Settings page banner.
    const [addError, setAddError]       = useState(null);
    const [editId, setEditId]           = useState(null);
    const [editForm, setEditForm]       = useState({});
    const [editError, setEditError]     = useState(null);
    const [loading, setLoading]         = useState(true);
    const [msg, setMsg]                 = useState(null);
    const [msgError, setMsgError]       = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
    // Stronger delete confirmation triggered from the Edit User modal.
    // Distinct from `confirmDelete` (row-level "Are you sure?") because this
    // path requires typing "DELETE" to enable the button.
    const [typeDelete, setTypeDelete] = useState(null); // { id, name } | null
    const [typeDeleteBusy, setTypeDeleteBusy] = useState(false);
    const { sortField, sortDir, onSort, sortRows } = useSortableTable('username');
    const { user: currentUser } = useAuth();
    const isAdmin = hasRole(currentUser, ['admin']);
    const callerUsername = currentUser?.user;
    // Extra permissions (Eric, 2026-10-08): the registry comes from the server
    // so a new item needs no change here. Only admins can grant them.
    const [permissionOptions, setPermissionOptions] = useState([]);
    useEffect(() => {
        if (!isAdmin) return;
        authenticatedFetch('/api/permissions')
            .then(r => (r.ok ? r.json() : { permissions: [] }))
            .then(d => setPermissionOptions(d.permissions || []))
            .catch(() => {});
    }, [isAdmin]);
    const ExtraPermissionsPicker = ({ value, onChange, roles }) => {
        if (!isAdmin || !permissionOptions.length) return null;
        const list = value || [];
        const toggle = (k) => onChange(list.includes(k) ? list.filter(x => x !== k) : [...list, k]);
        return (
            <div style={{ marginTop: 10 }}>
                <div className="settings-locations-label">Extra Permissions</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    {permissionOptions.map(p => {
                        // a permission that comes with one of the person's roles (e.g. Backend Reporting with Finance)
                        const viaRole = (p.default_roles || []).find(r => (roles || []).includes(r));
                        return (
                        <label key={p.key} title={p.description || ''} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: viaRole ? 'default' : 'pointer' }}>
                            <input type="checkbox" checked={viaRole ? true : list.includes(p.key)} disabled={!!viaRole} onChange={() => toggle(p.key)} />
                            <span>{p.label}{viaRole ? ` (comes with ${roleLabel(viaRole)})` : ''}</span>
                        </label>
                        );
                    })}
                </div>
                {(roles || []).includes('admin') && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6d7175' }}>Admins already have every permission.</div>
                )}
            </div>
        );
    };
    const roleOptions = isAdmin
        ? [
            { label: 'User',             value: 'user'             },
            { label: 'CRM',              value: 'crm'              },
            { label: 'Customer Service', value: 'customer_service' },
            { label: 'Finance',          value: 'finance'          },
            { label: 'External',         value: 'external'         },
            { label: 'Store Manager',    value: 'store_manager'    },
            { label: 'Franchise',        value: 'franchise'        },
            { label: 'Site Manager',     value: 'site_manager'     },
            { label: 'Admin',            value: 'admin'            },
          ]
        : [
            { label: 'User',         value: 'user'          },
          ];
    // customer_service users can only be edited/deleted by admins (parallel
    // to the admin-role gate). Mirrors the backend gate at app.py.
    const canEditUser = (u) => {
        if (hasRole(u, ['customer_service', 'finance', 'store_manager', 'external']) && !isAdmin) return false;
        return isAdmin || u.created_by === callerUsername;
    };
    const canDeleteUser = (u) => {
        if (hasRole(u, ['customer_service', 'finance', 'store_manager', 'external']) && !isAdmin) return false;
        return isAdmin || (u.created_by === callerUsername && u.created_by_role !== 'admin');
    };

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const [staff, setStaff]             = useState({ staff: [], links: {}, session_owner: null });
    const [staffBusy, setStaffBusy]     = useState(false);
    const loadStaff = async () => {
        try {
            const r = await authenticatedFetch('/api/admin/shopify-staff?store=soccerpost');
            if (r.ok) setStaff(await r.json());
        } catch (e) { /* column stays empty */ }
    };
    useEffect(() => { loadStaff(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const refreshStaff = async () => {
        setStaffBusy(true);
        try {
            const r = await authenticatedFetch('/api/admin/shopify-staff/refresh?store=soccerpost', { method: 'POST' });
            if (!r.ok) flash('Could not refresh staff from Shopify', true);
            await loadStaff();
            load();
        } finally { setStaffBusy(false); }
    };
    const setUserStaff = async (userId, value) => {
        const r = await authenticatedFetch(`/api/admin/users/${userId}/shopify-staff?store=soccerpost`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ staff_id: value ? Number(value) : null }),
        });
        if (!r.ok) flash('Could not update Shopify staff link', true);
        loadStaff();
    };

    const load = () => {
        setLoading(true);
        Promise.all([
            authenticatedFetch('/api/users').then(r => r.json()),
            ...STORE_KEYS.map(sk => authenticatedFetch(`/api/locations?store=${sk}`).then(r => r.json())),
        ]).then(([u, ...locArrays]) => {
            setUsers(u);
            const byStore = {};
            STORE_KEYS.forEach((sk, i) => {
                byStore[sk] = (locArrays[i] || []).filter(l => l.active);
            });
            setLocs(byStore);
        }).finally(() => setLoading(false));
    };

    useEffect(load, []);

    const assignmentLines = (assignments) => {
        if (!assignments || assignments.length === 0) return <span className="text-faint">—</span>;
        // site_manager: with the intersection-based LIST rule, a visible user may
        // have locations outside the SM's scope. Hide those rows in the Locations
        // column so the SM only sees the locations they share with that user.
        let scoped = assignments;
        if (hasRole(currentUser, ['site_manager']) && currentUser?.locations) {
            const own = new Set(currentUser.locations.map(l => `${l.store_key}:${l.id}`));
            scoped = assignments.filter(a => own.has(`${a.store_key}:${a.location_id}`));
            if (scoped.length === 0) return <span className="text-faint">—</span>;
        }
        let resolved = scoped.map(a => {
            const locs = allLocationsByStore[a.store_key] || [];
            const loc = locs.find(l => l.id === a.location_id);
            return {
                store_key: a.store_key,
                location_id: a.location_id,
                name: loc ? loc.name : (locationGroupFor(a)?.name || `ID:${a.location_id}`),
                prefix: STORE_DISPLAY_NAMES[a.store_key] || a.store_key,
                group: loc ? (a.store_key === 'soccerpost' ? spLocGroup(loc) : null) : (locationGroupFor(a)?.group || null),
            };
        }).sort((x, y) => x.name.localeCompare(y.name));
        // A hand-picked set that covers every member of a Soccer Post group reads as
        // the group ("All Corporate Stores (46)") instead of 46 lines.
        const spLocs = allLocationsByStore.soccerpost || [];
        for (const g of SP_LOCATION_GROUPS) {
            const members = spLocs.filter(l => spLocGroup(l) === g.group).map(l => l.id);
            const picked = resolved.filter(r => r.store_key === 'soccerpost' && r.location_id > 0 && r.group === g.group);
            if (members.length > 1 && picked.length === members.length && members.every(id => picked.some(r => r.location_id === id))) {
                resolved = resolved.filter(r => !picked.includes(r));
                resolved.unshift({ store_key: 'soccerpost', location_id: g.id, name: `${g.name} (${members.length})`, prefix: STORE_DISPLAY_NAMES.soccerpost, group: g.group });
            }
        }
        // Group rows first, then the rest; long lists fold behind "+N more".
        resolved.sort((x, y) => (y.location_id < 0) - (x.location_id < 0) || x.name.localeCompare(y.name));
        return <LocationLines rows={resolved} />;
    };

    // Non-admins only see their own locations in the assignment checkboxes
    const myLocSet = !isAdmin && currentUser?.locations
        ? new Set(currentUser.locations.map(l => `${l.store_key}:${l.id}`))
        : null;

    // For non-admin callers, hide store columns that have zero accessible
    // locations — a franchise user assigned only to Soccer Post shouldn't see
    // empty Surf / Rush / City Sports columns. Admins keep all 4 columns for a
    // consistent scan layout.
    const visibleStoreKeys = STORE_KEYS.filter(sk => {
        if (isAdmin) return true;
        const locs = allLocationsByStore[sk] || [];
        return locs.some(l => !myLocSet || myLocSet.has(`${sk}:${l.id}`));
    });
    // Roles (multi-select, 2026-10-05, Eric): a person may hold several roles and gets
    // the union of what they allow. A location-scoped role can be pinned to some of the
    // account's locations ("user at Teams Lancaster, store manager at Dublin"); with no
    // pin it applies at every location the account has.
    const RolesPicker = ({ roles, pins, assignments, onChange }) => {
        const locName = (a) => (allLocationsByStore[a.store_key] || []).find(l => l.id === a.location_id)?.name || locationGroupFor(a)?.name || `#${a.location_id}`;
        const toggleRole = (value) => {
            const next = roles.includes(value) ? roles.filter(r => r !== value) : [...roles, value];
            const nextPins = { ...pins }; if (!next.includes(value)) delete nextPins[value];
            onChange(next, nextPins);
        };
        const togglePin = (role, a) => {
            const cur = pins[role] || [];
            const has = cur.some(p => p.store_key === a.store_key && p.location_id === a.location_id);
            const next = has ? cur.filter(p => !(p.store_key === a.store_key && p.location_id === a.location_id)) : [...cur, { store_key: a.store_key, location_id: a.location_id }];
            const nextPins = { ...pins }; if (next.length) nextPins[role] = next; else delete nextPins[role];
            onChange(roles, nextPins);
        };
        return (
            <div>
                <div className="settings-locations-label">Roles</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    {roleOptions.map(o => (
                        <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                            <input type="checkbox" checked={roles.includes(o.value)} onChange={() => toggleRole(o.value)} />
                            <span>{o.label}</span>
                        </label>
                    ))}
                </div>
                {roles.filter(r => SCOPED_ROLES.includes(r)).length > 0 && assignments.length > 1 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#6d7175' }}>
                        Each role applies at every location below unless you limit it:
                        {roles.filter(r => SCOPED_ROLES.includes(r)).map(r => (
                            <div key={r} style={{ marginTop: 6 }}>
                                <strong style={{ color: '#202223' }}>{roleOptions.find(o => o.value === r)?.label || r}</strong>
                                {' — '}{(pins[r] || []).length ? 'only at:' : 'all locations'}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginTop: 2 }}>
                                    {assignments.map(a => (
                                        <label key={`${r}:${a.store_key}:${a.location_id}`} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            <input type="checkbox" checked={(pins[r] || []).some(p => p.store_key === a.store_key && p.location_id === a.location_id)} onChange={() => togglePin(r, a)} />
                                            <span>{locName(a)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const UsersLocationCheckboxes = ({ assignments, onChange }) => {
        // 2026-09-25 (Eric): location names never wrap. Each store column is as
        // wide as its longest name; stores with a long list (Soccer Post) stand
        // as full-height columns on the left, the short store columns wrap into
        // rows beside them, so every column stays inside the modal at any width.
        const columns = visibleStoreKeys.flatMap(sk => {
            let locs = allLocationsByStore[sk] || [];
            if (myLocSet) locs = locs.filter(l => myLocSet.has(`${sk}:${l.id}`));
            // Alphabetical within each column so admins scan by name.
            locs = [...locs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            // 2026-10-15 (Eric): Soccer Post is the only store with corporate,
            // franchise, Teams and warehouse locations — split its column into
            // Warehouses & Teams / Corporate / Franchise, each section headed by
            // its "All …" group row (admins only). Other stores keep one column.
            if (sk === 'soccerpost') {
                const by = { warehouse: [], teams: [], corporate: [], franchise: [] };
                locs.forEach(l => by[spLocGroup(l)].push(l));
                const grp = (gid) => SP_LOCATION_GROUPS.find(g => g.id === gid);
                const name = STORE_DISPLAY_NAMES[sk];
                return [
                    { sk, key: `${sk}:ops`, title: `${name} · Warehouses & Teams`,
                      sections: [{ group: grp(-3), locs: by.warehouse }, { group: grp(-2), locs: by.teams }] },
                    { sk, key: `${sk}:corporate`, title: `${name} · Corporate`, sections: [{ group: grp(-1), locs: by.corporate }] },
                    { sk, key: `${sk}:franchise`, title: `${name} · Franchise`, sections: [{ group: grp(-4), locs: by.franchise }] },
                ].map(c => ({ ...c, sections: c.sections.filter(x => x.locs.length > 0), locs: c.sections.flatMap(x => x.locs) }))
                 .filter(c => c.locs.length > 0);
            }
            return [{ sk, key: sk, title: STORE_DISPLAY_NAMES[sk], sections: [{ group: null, locs }], locs }];
        });
        const has = (sk, id) => assignments.some(a => a.store_key === sk && a.location_id === id);
        const toggle = (sk, id) => onChange(has(sk, id)
            ? assignments.filter(a => !(a.store_key === sk && a.location_id === id))
            : [...assignments, { store_key: sk, location_id: id }]);
        const rowStyle = {
            display: 'flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', cursor: 'pointer', marginBottom: '4px',
            lineHeight: '1.35', whiteSpace: 'nowrap',
        };
        const tall = columns.filter(c => c.locs.length > 8);
        const short = columns.filter(c => c.locs.length <= 8);
        const renderColumn = ({ sk, key, title, sections, locs }) => (
            <div key={key} style={{ flex: '0 0 auto' }}>
                <div style={{
                    fontSize: '12px', fontWeight: 700, color: '#374151',
                    marginBottom: '8px', paddingBottom: '6px',
                    borderBottom: '2px solid #00798E',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                    {title}
                </div>
                {locs.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>No locations</div>
                ) : sections.map((sec, si) => {
                    // Group rows are an admin tool; a scoped caller only sees its own locations.
                    const g = sec.group && !myLocSet ? sec.group : null;
                    const implied = !!(g && has(sk, g.id));
                    return (
                        <div key={si} style={{ marginBottom: si < sections.length - 1 ? '10px' : 0 }}>
                            {g && (
                                <label style={{ ...rowStyle, fontWeight: 600 }} title="Every current and future location of this kind">
                                    <input type="checkbox" checked={implied} onChange={() => toggle(sk, g.id)} />
                                    <span>{g.name}</span>
                                </label>
                            )}
                            {sec.locs.map(loc => (
                                <label key={loc.id} style={{ ...rowStyle, paddingLeft: g ? '14px' : 0, color: implied ? '#6d7175' : undefined }}>
                                    <input
                                        type="checkbox"
                                        checked={implied || has(sk, loc.id)}
                                        disabled={implied}
                                        onChange={() => toggle(sk, loc.id)}
                                    />
                                    <span>{loc.name}</span>
                                </label>
                            ))}
                        </div>
                    );
                })}
            </div>
        );
        return (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '28px', flexWrap: 'wrap' }}>
                {tall.map(renderColumn)}
                {short.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '20px 28px', flex: '1 1 0', minWidth: 0 }}>
                        {short.map(renderColumn)}
                    </div>
                )}
            </div>
        );
    };

    // 2026-08-03: usernames allowed to add users with non-@soccerpost.com
    // emails. Must match _EMAIL_DOMAIN_BYPASS_USERS in the backend.
    const _emailDomainBypass = callerUsername === 'eric';

    const handleAdd = async () => {
        setAddError(null);
        if (!form.username?.trim())        return setAddError('Username is required.');
        if (!form.email?.trim())           return setAddError('Email is required — the temporary password is emailed to the user.');
        if (!_emailDomainBypass && !/@soccerpost\.com$/i.test(form.email.trim())) {
            return setAddError('Email must be a @soccerpost.com address.');
        }
        if (!(form.roles || []).length)    return setAddError('At least one role is required.');
        if (rolesRequireLocation(form.roles) && !(form.assignments || []).length) return setAddError(LOCATION_REQUIRED_MSG);
        try {
            // Intentionally strip `password` — backend auto-generates a 16-char
            // temp password and emails it. Admin no longer picks the initial
            // password directly.
            const { password: _drop, ...body } = form;
            body.role = (body.roles || [])[0];
            body.role_locations = _cleanPins(body.role_locations, body.roles, expandAssignments(body.assignments, allLocationsByStore));
            const res = await authenticatedFetch('/api/users', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                const sent = data?.temp_password_emailed;
                flash(sent ? `User created and invite emailed to ${form.email}.`
                           : `User created, but the invite email could not be sent to ${form.email}. Use Resend Invite.`, !sent);
                setShowAdd(false); setForm(EMPTY_USER_FORM); setAddError(null); setDuplicateFromName(''); load();
            }
            else {
                const e = await res.json().catch(() => ({}));
                setAddError(e.detail || 'Failed to create user.');
            }
        } catch (err) { setAddError(err.message || 'Network error. Try again.'); }
    };

    const handleUpdate = async (id) => {
        setEditError(null);
        const body = { ...editForm };
        if (body.email && !_emailDomainBypass && !/@soccerpost\.com$/i.test(body.email.trim())) {
            return setEditError('Email must be a @soccerpost.com address.');
        }
        if (!body.password) delete body.password;
        else {
            const pwErr = validatePasswordComplexity(body.password);
            if (pwErr) return setEditError(pwErr);
        }
        if (!(body.roles || []).length) return setEditError('At least one role is required.');
        if (rolesRequireLocation(body.roles) && !(body.assignments || []).length) return setEditError(LOCATION_REQUIRED_MSG);
        body.role = body.roles[0];
        body.role_locations = _cleanPins(body.role_locations, body.roles, expandAssignments(body.assignments, allLocationsByStore));
        const res = await authenticatedFetch(`/api/users/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        if (res.ok) { flash('User updated.'); setEditId(null); setEditError(null); load(); }
        else { const e = await res.json().catch(() => ({})); setEditError(e.detail || 'Failed to update user.'); }
    };

    // 2026-08-03: route the row-level Delete through the "TYPE DELETE"
    // confirmation to match the Edit-modal Delete flow. Single destructive
    // pattern for the Users tab, so an admin can't one-click a delete.
    const handleDelete = (id, name) => setTypeDelete({ id, name });

    const handleResendInvite = async (u) => {
        if (!window.confirm(`Email a new temporary password to ${u.email}? Their current sessions will be signed out.`)) return;
        try {
            const res = await authenticatedFetch(`/api/users/${u.id}/reset-password`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                flash(data?.email_sent ? `Invite emailed to ${u.email}.` : `Password reset, but email failed to send to ${u.email}.`, !data?.email_sent);
            } else {
                const e = await res.json().catch(() => ({}));
                flash(`Error: ${e.detail || 'Failed to resend invite.'}`, true);
            }
        } catch (err) { flash(`Error: ${err.message}`, true); }
    };

    const confirmDeleteUser = async () => {
        await authenticatedFetch(`/api/users/${confirmDelete.id}`, { method: 'DELETE' });
        setConfirmDelete(null);
        load();
    };

    // Delete from inside the Edit User modal — routes through the type-to-
    // confirm flow ("TYPE DELETE") rather than the simple row-level confirm.
    const handleTypeDeleteConfirm = async () => {
        if (!typeDelete) return;
        setTypeDeleteBusy(true);
        try {
            const res = await authenticatedFetch(`/api/users/${typeDelete.id}`, { method: 'DELETE' });
            if (!res.ok) {
                let detail = 'Delete failed';
                try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch {}
                flash(detail, true);
            } else {
                setTypeDelete(null);
                setEditId(null);
                setEditError(null);
                load();
            }
        } catch (e) {
            flash(`Delete failed: ${e.message}`, true);
        } finally {
            setTypeDeleteBusy(false);
        }
    };

    return (
        <>
        {confirmDelete && (
            <DeleteConfirmModal
                title="Delete User"
                message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
                onConfirm={confirmDeleteUser}
                onCancel={() => setConfirmDelete(null)}
            />
        )}
        {typeDelete && (
            <TypeToConfirmModal
                title={`Delete User: ${typeDelete.name}`}
                message={`This will permanently delete "${typeDelete.name}" and cannot be undone.`}
                confirmPhrase="DELETE"
                confirmLabel="Delete User"
                busy={typeDeleteBusy}
                onConfirm={handleTypeDeleteConfirm}
                onCancel={() => setTypeDelete(null)}
            />
        )}
        {editId !== null && (() => {
            const _u = users.find(u => u.id === editId);
            const _canDelete = _u && canEditUser(_u) && _u.username !== callerUsername;
            const _secondary = [{ content: 'Cancel', onAction: () => { setEditId(null); setEditError(null); } }];
            if (_canDelete) {
                _secondary.unshift({
                    content: 'Delete User',
                    destructive: true,
                    // Close the Edit modal first — Polaris's focus trap on the
                    // still-open Edit modal blocks keystrokes into the type-to-
                    // confirm input. On cancel we don't re-open the Edit modal;
                    // the user can click Edit again if they change their mind.
                    onAction: () => {
                        const _target = { id: _u.id, name: _u.username };
                        setEditId(null);
                        setEditError(null);
                        setTypeDelete(_target);
                    },
                });
            }
            return (
            <Modal
                open={true}
                size="large"
                onClose={() => { setEditId(null); setEditError(null); }}
                title={`Edit User: ${_u?.username || ''}`}
                primaryAction={{ content: 'Save', onAction: () => handleUpdate(editId) }}
                secondaryActions={_secondary}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {editError && (
                            <Banner tone="critical" onDismiss={() => setEditError(null)}>{editError}</Banner>
                        )}
                        <div className="settings-grid-4">
                            <TextField label="First Name" value={editForm.first_name || ''} onChange={v => setEditForm(f => ({ ...f, first_name: v }))} autoComplete="off" />
                            <TextField label="Last Name" value={editForm.last_name || ''} onChange={v => setEditForm(f => ({ ...f, last_name: v }))} autoComplete="off" />
                            <TextField label="Email" type="email" value={editForm.email || ''} onChange={v => setEditForm(f => ({ ...f, email: v }))} autoComplete="off" />
                            <TextField label="New Password" type="password" value={editForm.password || ''} onChange={v => setEditForm(f => ({ ...f, password: v }))} placeholder="Blank = keep current" autoComplete="new-password" helpText={PASSWORD_RULES_HINT} />
                        </div>
                        <InlineStack gap="400" wrap={false}>
                            <div style={{ flex: 1 }}>
                                <RolesPicker roles={editForm.roles || []} pins={editForm.role_locations || {}} assignments={expandAssignments(editForm.assignments, allLocationsByStore)}
                                             onChange={(roles, pins) => setEditForm(f => ({ ...f, roles, role: roles[0], role_locations: pins }))} />
                                <ExtraPermissionsPicker value={editForm.permissions || []} roles={editForm.roles || []}
                                                        onChange={v => setEditForm(f => ({ ...f, permissions: v }))} />
                            </div>
                            <div style={{ paddingTop: '24px' }}>
                                <Checkbox label="Active" checked={!!editForm.active} onChange={v => setEditForm(f => ({ ...f, active: v }))} />
                            </div>
                        </InlineStack>
                        <div>
                            <div className="settings-locations-label">Locations{rolesRequireLocation(editForm.roles) ? ' (required for one of these roles)' : ''}</div>
                            <UsersLocationCheckboxes assignments={editForm.assignments || []} onChange={v => setEditForm(f => ({ ...f, assignments: v }))} />
                        </div>
                    </BlockStack>
                </Modal.Section>
            </Modal>
            );
        })()}
        {showAdd && (
            <Modal
                open={true}
                size="large"
                onClose={() => { setShowAdd(false); setForm(EMPTY_USER_FORM); setAddError(null); setDuplicateFromName(''); }}
                title={duplicateFromName ? `Add User (duplicating settings from ${duplicateFromName})` : 'Add User'}
                primaryAction={{ content: 'Create User', onAction: handleAdd }}
                secondaryActions={[{ content: 'Cancel', onAction: () => { setShowAdd(false); setForm(EMPTY_USER_FORM); setAddError(null); setDuplicateFromName(''); } }]}
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {addError && (
                            <Banner tone="critical" onDismiss={() => setAddError(null)}>{addError}</Banner>
                        )}
                        <div className="settings-grid-4">
                            <TextField label="First Name" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} autoComplete="off" />
                            <TextField label="Last Name" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} autoComplete="off" />
                            <TextField label="Email" type="email" requiredIndicator value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} autoComplete="off" helpText={_emailDomainBypass ? "A temporary password is emailed to this address." : "A temporary password is emailed to this address. Must be @soccerpost.com."} />
                            <TextField label="Username" requiredIndicator value={form.username} onChange={v => setForm(f => ({ ...f, username: v }))} autoComplete="off" />
                        </div>
                        <div className="settings-grid-4">
                            <div>
                                <RolesPicker roles={form.roles || []} pins={form.role_locations || {}} assignments={expandAssignments(form.assignments, allLocationsByStore)}
                                             onChange={(roles, pins) => setForm(f => ({ ...f, roles, role: roles[0], role_locations: pins }))} />
                                <ExtraPermissionsPicker value={form.permissions || []} roles={form.roles || []}
                                                        onChange={v => setForm(f => ({ ...f, permissions: v }))} />
                            </div>
                        </div>
                        <div>
                            <div className="settings-locations-label">Locations{rolesRequireLocation(form.roles) ? ' (required for one of these roles)' : ''}</div>
                            <UsersLocationCheckboxes assignments={form.assignments || []} onChange={v => setForm(f => ({ ...f, assignments: v }))} />
                        </div>
                    </BlockStack>
                </Modal.Section>
            </Modal>
        )}
        <BlockStack gap="400">
            {msg && (
                <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>
            )}

            <div>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingSm" as="h3">Users</Text>
                        <InlineStack gap="200">
                            <Button size="slim" loading={staffBusy} onClick={refreshStaff}>Refresh staff from Shopify</Button>
                            <Button variant="primary" onClick={() => setShowAdd(true)}>Add User</Button>
                        </InlineStack>
                    </InlineStack>

                    <InlineStack gap="300" align="start" blockAlign="end" wrap>
                        <div style={{ flex: '1 1 300px', maxWidth: 360 }}>
                            <TextField
                                label="Filter by name, username, or email"
                                labelHidden
                                placeholder="Filter by name, username, or email"
                                value={nameFilter}
                                onChange={setNameFilter}
                                clearButton
                                onClearButtonClick={() => setNameFilter('')}
                                autoComplete="off"
                            />
                        </div>
                        <div style={{ flex: '0 0 220px', minWidth: 200 }}>
                            <Select
                                label="Filter by user type"
                                labelHidden
                                options={[
                                    { label: 'All user types', value: 'all' },
                                    ...roleOptions,
                                ]}
                                value={roleFilter}
                                onChange={setRoleFilter}
                            />
                        </div>
                        <div style={{ flex: '0 0 300px', minWidth: 240 }}>
                            <Select
                                label="Filter by location"
                                labelHidden
                                options={[
                                    { label: 'All locations', value: 'all' },
                                    { label: 'No locations assigned', value: 'none' },
                                    ...visibleStoreKeys.flatMap(sk => {
                                        let locs = allLocationsByStore[sk] || [];
                                        if (myLocSet) locs = locs.filter(l => myLocSet.has(`${sk}:${l.id}`));
                                        return [...locs]
                                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                            .map(l => ({ label: `${STORE_DISPLAY_NAMES[sk] || sk}: ${l.name}`, value: `${sk}:${l.id}` }));
                                    }),
                                ]}
                                value={locFilter}
                                onChange={setLocFilter}
                            />
                        </div>
                    </InlineStack>

                    {loading ? (
                        <Text tone="subdued">Loading…</Text>
                    ) : (
                        <div className="settings-users-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}>
                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <SortableHeader label="Username" field="username" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <SortableHeader label="Name" field="name" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <SortableHeader label="Email" field="email" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <SortableHeader label="Role" field="role" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <SortableHeader label="Locations" field="locations" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <th>Shopify staff</th>
                                    <th>Added By</th>
                                    <SortableHeader label="Last use" field="last_login_at" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortRows(users.filter(u => {
                                    if (roleFilter !== 'all' && !userRoles(u).includes(roleFilter)) return false;
                                    if (locFilter === 'none' && (u.assignments || []).length > 0) return false;
                                    if (locFilter !== 'all' && locFilter !== 'none'
                                        && !expandAssignments(u.assignments, allLocationsByStore).some(a => `${a.store_key}:${a.location_id}` === locFilter)) return false;
                                    const q = nameFilter.trim().toLowerCase();
                                    if (!q) return true;
                                    const hay = [
                                        u.username, u.first_name, u.last_name, u.email,
                                        `${u.first_name || ''} ${u.last_name || ''}`,
                                    ].filter(Boolean).join(' ').toLowerCase();
                                    return hay.includes(q);
                                }), (u, f) => {
                                    if (f === 'name') return [u.first_name, u.last_name].filter(Boolean).join(' ');
                                    if (f === 'locations') {
                                        // Sort by the first assigned location's name, alphabetically.
                                        // Users with no assignments sink to the end.
                                        const names = (u.assignments || [])
                                            .map(a => {
                                                const locs = allLocationsByStore[a.store_key] || [];
                                                const loc = locs.find(l => l.id === a.location_id);
                                                return (loc?.name || '').toLowerCase();
                                            })
                                            .filter(Boolean)
                                            .sort();
                                        return names[0] || '\uffff'; // empty sorts last
                                    }
                                    return u[f];
                                }).map(u => (
                                    <React.Fragment key={u.id}>
                                        {/* Normal display row */}
                                        <tr>
                                            <td className="td-username">{u.username}</td>
                                            <td className="td-name-nowrap">
                                                {[u.first_name, u.last_name].filter(Boolean).join(' ') || <span className="text-faint">—</span>}
                                            </td>
                                            <td className="td-email">{u.email || <span className="text-faint">—</span>}</td>
                                            <td>
                                                <InlineStack gap="100" wrap>
                                                    {userRoles(u).map(r => <Badge key={r} tone={r === 'admin' ? 'info' : undefined}>{r}</Badge>)}
                                                </InlineStack>
                                            </td>
                                            <td className="td-location">
                                                {assignmentLines(u.assignments)}
                                            </td>
                                            <td className="td-nowrap">
                                                <Select label="Shopify staff" labelHidden
                                                    options={[{ label: 'Not linked', value: '' }, ...(staff.staff || [])
                                                        .filter(s => s.active || String(s.staff_id) === String(staff.links?.[u.id]?.staff_id))
                                                        .map(s => ({ label: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email, value: String(s.staff_id) }))]}
                                                    value={String(staff.links?.[u.id]?.staff_id || '')}
                                                    onChange={(v) => setUserStaff(u.id, v)} />
                                                <div className="text-faint" style={{ fontSize: 11 }}>
                                                    {staff.links?.[u.id] ? (staff.links[u.id].matched_by === 'email' ? 'by email' : 'set by admin') : 'OpsHub only'}
                                                </div>
                                            </td>
                                            <td>
                                                {u.created_by_display ? `${u.created_by_display} (${u.created_by_role || '?'})` : <span className="text-faint">—</span>}
                                            </td>
                                            <td className="td-muted" title={u.last_login_at || 'Never'}>
                                                {lastUseText(u.last_login_at)}
                                            </td>
                                            <td>
                                                <Badge tone={u.active ? 'success' : undefined}>{u.active ? 'Active' : 'Inactive'}</Badge>
                                            </td>
                                            <td className="td-nowrap">
                                                <InlineStack gap="200" wrap={false}>
                                                    {canEditUser(u) && (
                                                        <Button size="slim" onClick={() => {
                                                            setEditId(u.id);
                                                            setEditForm({ role: u.role, roles: userRoles(u), role_locations: u.role_locations || {}, active: u.active, password: '',
                                                                first_name: u.first_name || '', last_name: u.last_name || '',
                                                                email: u.email || '', assignments: u.assignments || [], permissions: u.permissions || [] });
                                                        }}>Edit</Button>
                                                    )}
                                                    {canEditUser(u) && u.email && (
                                                        <Button size="slim" onClick={() => handleResendInvite(u)}>Resend Invite</Button>
                                                    )}
                                                    {canEditUser(u) && (
                                                        <Button size="slim" onClick={() => {
                                                            setEditId(null);
                                                            setDuplicateFromName(`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username);
                                                            setForm({
                                                                ...EMPTY_USER_FORM,
                                                                roles: userRoles(u),
                                                                role: userRoles(u)[0] || 'user',
                                                                role_locations: u.role_locations || {},
                                                                assignments: (u.assignments || []).map(a => ({ store_key: a.store_key, location_id: a.location_id })),
                                                                permissions: isAdmin ? (u.permissions || []) : [],
                                                            });
                                                            setAddError(null);
                                                            setShowAdd(true);
                                                        }}>Duplicate</Button>
                                                    )}
                                                    {canDeleteUser(u) && (
                                                        <Button size="slim" tone="critical" onClick={() => handleDelete(u.id, u.username)}>Delete</Button>
                                                    )}
                                                </InlineStack>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}

                </BlockStack>
            </div>
        </BlockStack>
        </>
    );
}

// ── Shopify Session tab ───────────────────────────────────────────────────────

function SessionStatusConfigured({ status, fmtDate }) {
    const now            = new Date();
    const expiresDate    = status.expires_at ? new Date(status.expires_at) : null;
    const isExpired      = expiresDate && expiresDate <= now;
    const isExpiringSoon = expiresDate && !isExpired && (expiresDate - now) < 7 * 24 * 60 * 60 * 1000;
    const staff          = status.shopify_staff || null;
    const staffName      = staff
        ? [staff.firstName, staff.lastName].filter(Boolean).join(' ').trim() || staff.email
        : null;

    const Row = ({ label, children }) => (
        <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '4px 0', alignItems: 'baseline' }}>
            <div style={{ width: 160, color: '#6d7175', flexShrink: 0 }}>{label}</div>
            <div style={{ color: '#202223' }}>{children}</div>
        </div>
    );

    return (
        <div className="settings-session-status">
            <div className="settings-session-status__row">
                <span className={`settings-status-dot ${isExpired ? 'settings-status-dot--expired' : isExpiringSoon ? 'settings-status-dot--expiring' : 'settings-status-dot--ok'}`} />
                <Text>{isExpired
                    ? 'Session expired — upload a fresh HAR'
                    : isExpiringSoon
                        ? 'Session valid (expiring soon)'
                        : 'Session valid'}</Text>
            </div>
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f6f6f7', borderRadius: 8 }}>
                <Row label="Shopify staff">
                    {staff
                        ? <span><strong>{staffName}</strong>{staff.email ? <span style={{ color: '#6d7175' }}> &nbsp;·&nbsp; {staff.email}</span> : null}</span>
                        : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>not yet probed — refreshes on next session push</span>}
                </Row>
                <Text as="p" tone="subdued" variant="bodySm">
                    Comments posted from OpsHub appear in Shopify as {staffName || 'the session owner'}, prefixed "[OpsHub: author]".
                </Text>
                <Row label="Uploaded by">
                    {status.uploaded_by
                        ? <span><strong>{status.uploaded_by}</strong>{status.uploaded_at ? <span style={{ color: '#6d7175' }}> &nbsp;·&nbsp; {fmtDate(status.uploaded_at)}</span> : null}</span>
                        : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>}
                </Row>
                <Row label="Cookies / source">
                    {status.cookie_count} cookies &nbsp;·&nbsp; {status.source}
                </Row>
                <Row label="Expires">
                    <span className={isExpired ? 'settings-expiry-text--expired' : isExpiringSoon ? 'settings-expiry-text--expiring' : ''}>
                        {expiresDate ? fmtDate(status.expires_at) : '—'}
                    </span>
                </Row>
            </div>
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f1f7ff', borderRadius: 8 }}>
                <Row label="Last label printed">
                    {status.last_label_at
                        ? <span>{fmtDate(status.last_label_at)}
                              {status.last_label_printed_by
                                  ? <span style={{ color: '#6d7175' }}> &nbsp;·&nbsp; by <strong>{status.last_label_printed_by}</strong></span>
                                  : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}> &nbsp;·&nbsp; operator unknown</span>}
                              {status.last_label_location_name
                                  ? <span style={{ color: '#6d7175' }}> &nbsp;·&nbsp; at <strong>{status.last_label_location_name}</strong></span>
                                  : null}
                          </span>
                        : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>none today</span>}
                </Row>
                <Row label="Labels printed today">
                    <strong>{status.labels_today ?? 0}</strong>
                </Row>
                {(status.quarantined_hashes ?? 0) > 0 && (
                    <Row label="Quarantined hashes">
                        <span style={{ color: '#d97706' }}>{status.quarantined_hashes} active operation hash{status.quarantined_hashes > 1 ? 'es' : ''} flagged stale</span>
                    </Row>
                )}
            </div>
        </div>
    );
}

function ShopifySessionTab() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, ['admin']);
    const [searchParams, setSearchParams] = useSearchParams();
    const urlStore = searchParams.get('store');
    const [storeKey, setStoreKey] = useState(
        urlStore && STORE_KEYS.includes(urlStore) ? urlStore : STORE_KEYS[0]
    );
    useEffect(() => {
        if (urlStore && STORE_KEYS.includes(urlStore) && urlStore !== storeKey) setStoreKey(urlStore);
    }, [urlStore]); // eslint-disable-line
    const setStoreAndUrl = (sk) => {
        setStoreKey(sk);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'session');
            next.set('store', sk);
            return next;
        }, { replace: true });
    };
    const [status, setStatus]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [msg, setMsg]         = useState(null);
    const [msgError, setMsgError] = useState(false);

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const loadStatus = () => {
        setLoading(true);
        authenticatedFetch(`/api/settings/shopify-session?store=${storeKey}`)
            .then(r => r.json()).then(setStatus).finally(() => setLoading(false));
    };

    useEffect(() => { loadStatus(); }, [storeKey]);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMsg(null);
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await authenticatedFetch(`/api/settings/shopify-session?store=${storeKey}`, { method: 'POST', body: form });
            let data = null;
            try { data = await res.json(); } catch {}
            if (res.ok && data) {
                flash(`Session updated — ${data.cookie_count} cookies extracted.`);
                loadStatus();
            } else {
                flash(data?.detail || (res.status === 413 ? 'File too large' : `Upload failed (HTTP ${res.status})`), true);
            }
        } catch (e) { flash(e.message, true); }
        finally { setUploading(false); e.target.value = ''; }
    };

    const fmtDate = (iso) => iso
        ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '—';

    return (
        <StoreSubTabsPanel storeKey={storeKey} onStoreChange={setStoreAndUrl}>
        <BlockStack gap="400">
            {msg && <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>}

            {/* ── Shipping Labels session ── */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingSm" as="h3">Shipping Labels Session</Text>
                    {loading ? (
                        <Text tone="subdued">Checking…</Text>
                    ) : status?.configured ? (
                        <SessionStatusConfigured status={status} fmtDate={fmtDate} />
                    ) : (
                        <div className="settings-session-status__row">
                            <span className="settings-status-dot settings-status-dot--error" />
                            <Text tone="critical">No session configured — shipping labels will not work until a HAR is uploaded.</Text>
                        </div>
                    )}
                </BlockStack>
            </Card>

            <Card>
                <BlockStack gap="300">
                    <Text variant="headingSm" as="h3">Chrome Extension (Recommended)</Text>
                    <Text tone="subdued" as="p">
                        Automatically syncs your Shopify admin session while you browse — no manual HAR uploads needed.
                    </Text>
                    <div className="settings-instructions-box">
                        <Text variant="headingSm" as="h4">How to install</Text>
                        <ol>
                            <li>Download and <strong>unzip</strong> the extension</li>
                            <li>Open Chrome → <code>chrome://extensions</code></li>
                            <li>Enable <strong>Developer mode</strong> (top-right toggle)</li>
                            <li>Click <strong>Load unpacked</strong> → select the unzipped folder</li>
                            <li>Browse Shopify admin normally — sessions sync automatically every 5 min</li>
                            <li><strong>Do not move, rename, or delete the extracted folder</strong> after loading — Chrome reads the files from that exact location. If you need to move it, remove the extension from <code>chrome://extensions</code> first, then reload it from the new location.</li>
                        </ol>
                    </div>
                    <div>
                        <a href="/api/ext/shopify-session-extension.zip" download className="settings-har-upload-label" style={{ textDecoration: 'none', display: 'inline-block' }}>
                            Download Extension
                        </a>
                    </div>
                </BlockStack>
            </Card>

            {isAdmin && (
                <Card>
                    <BlockStack gap="300">
                        <Text variant="headingSm" as="h3">Upload Shipping Labels HAR (Manual)</Text>
                        <Text tone="subdued" as="p">
                            If you prefer not to use the extension, you can manually capture and upload a HAR file from Shopify admin.
                        </Text>
                        <div className="settings-instructions-box">
                            <Text variant="headingSm" as="h4">How to capture</Text>
                            <ol>
                                <li>Open <strong>Chrome</strong>, go to <strong>admin.shopify.com</strong>, log in as the {STORE_DISPLAY_NAMES[storeKey]} admin account</li>
                                <li>Open DevTools → <strong>Network</strong> tab, check <strong>"Preserve log"</strong></li>
                                <li>Click around Shopify admin (Orders, Settings, etc.)</li>
                                <li>Right-click any request → <strong>Save all as HAR with content</strong></li>
                                <li>Upload below</li>
                            </ol>
                        </div>
                        <div>
                            <label className={`settings-har-upload-label${uploading ? ' settings-har-upload-label--uploading' : ''}`}>
                                {uploading ? 'Uploading…' : '⬆ Upload HAR File'}
                                <input type="file" accept=".har,application/json" disabled={uploading} onChange={handleFileUpload} />
                            </label>
                        </div>
                    </BlockStack>
                </Card>
            )}

        </BlockStack>
        </StoreSubTabsPanel>
    );
}

// ── Picker / Presser / Packing Users tab ──────────────────────────────────────────

const EMPTY_PRESSER_FORM = {
    first_name: '', last_name: '', pin: '', active: true, roles: ['picker'],
    scan_only_picker: false,
    scan_only_presser: false,
    scan_only_packer: false,
    is_manager: false,
    assignments: [],
};
// store_manager creates 'store'-only PIN users, never with a scan-only override.
const EMPTY_STORE_MANAGER_FORM = {
    ...EMPTY_PRESSER_FORM,
    roles: ['store'],
    is_manager: false,
};
const ROLE_LABELS = { picker: 'Picker', presser: 'Presser', packing: 'Packer', store: 'Store', warehouse: 'Warehouse', bulk_shipping: 'Bulk Shipping' };

// Scrollable multi-store location checkbox picker (Task 9)
function ScrollableLocationCheckboxes({ assignments, onChange, allLocationsByStore, myLocSet }) {
    const [filter, setFilter] = React.useState('');
    const filterLc = filter.trim().toLowerCase();

    // Flatten for total + selected counts.
    const allRows = [];
    for (const sk of STORE_KEYS) {
        const locs = (allLocationsByStore[sk] || []).filter(l => !myLocSet || myLocSet.has(l.shopify_location_id));
        for (const loc of locs) allRows.push({ sk, loc });
    }
    const visibleRows = filterLc
        ? allRows.filter(r => (r.loc.name || '').toLowerCase().includes(filterLc))
        : allRows;
    const selectedCount = assignments.length;

    const isSelected = (sk, sloc) => assignments.some(a => a.store_key === sk && a.shopify_location_id === sloc);
    const toggle = (sk, sloc) => {
        if (isSelected(sk, sloc)) {
            onChange(assignments.filter(a => !(a.store_key === sk && a.shopify_location_id === sloc)));
        } else {
            onChange([...assignments, { store_key: sk, shopify_location_id: sloc }]);
        }
    };

    const selectAllInStore = (sk) => {
        const locs = (allLocationsByStore[sk] || []).filter(l => !myLocSet || myLocSet.has(l.shopify_location_id));
        const toAdd = locs
            .filter(l => !isSelected(sk, l.shopify_location_id))
            .map(l => ({ store_key: sk, shopify_location_id: l.shopify_location_id }));
        if (toAdd.length) onChange([...assignments, ...toAdd]);
    };

    // Group visible rows by store_key for sticky headers.
    const byStore = {};
    for (const r of visibleRows) (byStore[r.sk] ||= []).push(r.loc);

    return (
        <div className="loc-picker">
            <div className="loc-toolbar">
                <input className="loc-search" placeholder="Filter locations..." value={filter} onChange={e => setFilter(e.target.value)} />
                <span className="loc-count"><strong>{selectedCount}</strong> of {allRows.length} selected</span>
                <button type="button" className="loc-linkbtn" onClick={() => onChange([])}>Clear</button>
            </div>
            <div className="loc-scroll">
                {STORE_KEYS.map(sk => {
                    const locs = byStore[sk] || [];
                    if (!locs.length) return null;
                    // Partition Teams locations (is_store=false) above, Store locations (is_store=true) below.
                    const teamsLocs = locs.filter(l => !l.is_store);
                    const storeLocs = locs.filter(l =>  l.is_store);
                    const renderRow = (loc) => (
                        <label key={loc.shopify_location_id} className="loc-row">
                            <input type="checkbox"
                                checked={isSelected(sk, loc.shopify_location_id)}
                                onChange={() => toggle(sk, loc.shopify_location_id)} />
                            {loc.name}
                        </label>
                    );
                    return (
                        <div className="loc-store-group" key={sk}>
                            <div className="loc-store-head">
                                <span>{STORE_DISPLAY_NAMES[sk] || sk}</span>
                                <button type="button" className="loc-linkbtn loc-group-all" onClick={() => selectAllInStore(sk)}>
                                    Select all in store
                                </button>
                            </div>
                            {teamsLocs.map(renderRow)}
                            {teamsLocs.length > 0 && storeLocs.length > 0 && (
                                <div className="loc-partition" aria-hidden="true" />
                            )}
                            {storeLocs.map(renderRow)}
                        </div>
                    );
                })}
                {visibleRows.length === 0 && (
                    <div style={{ padding: '12px', color: '#6d7175', fontSize: '12px', fontStyle: 'italic' }}>
                        No locations match "{filter}"
                    </div>
                )}
            </div>
        </div>
    );
}
const ROLES_WITH_BEHAVIOR = [
    { role: 'picker',  label: 'Picker',  flag: 'scan_only_picker' },
    { role: 'presser', label: 'Presser', flag: 'scan_only_presser' },
    { role: 'packing', label: 'Packer',  flag: 'scan_only_packer' },
];

function RolesAndBehavior({ form, setForm }) {
    const toggleRole = (r, on) => setForm(f => ({
        ...f,
        roles: on ? [...(f.roles || []), r]
                  : (f.roles || []).filter(x => x !== r && !(r === 'warehouse' && x.startsWith('warehouse.'))),   // turning Warehouse off drops its extras
    }));
    const setFlag = (flag, val) => setForm(f => ({ ...f, [flag]: val }));

    return (
        <div>
            <label className="settings-field-label">Roles &amp; Behavior</label>
            <div className="roles-wrap">
                {ROLES_WITH_BEHAVIOR.map(({ role, label, flag }) => {
                    const enabled = (form.roles || []).includes(role);
                    return (
                        <div className={`role-row ${enabled ? '' : 'role-row--off'}`} key={role}>
                            <label className="role-cb">
                                <input type="checkbox" checked={enabled} onChange={e => toggleRole(role, e.target.checked)} />
                                <span className={enabled ? '' : 'role-cb--dim'}>{label}</span>
                            </label>
                            {enabled ? (
                                <select className="role-select"
                                    value={form[flag] ? 'scan' : 'manual'}
                                    onChange={e => setFlag(flag, e.target.value === 'scan')}>
                                    <option value="manual">Manual + Scan</option>
                                    <option value="scan">Scan only</option>
                                </select>
                            ) : (
                                <span className="role-hint">— enable to configure —</span>
                            )}
                        </div>
                    );
                })}
                {/* Store role — checkbox only, no behavior dropdown */}
                <div className={`role-row ${(form.roles || []).includes('store') ? '' : 'role-row--off'}`}>
                    <label className="role-cb">
                        <input type="checkbox" checked={(form.roles || []).includes('store')}
                            onChange={e => toggleRole('store', e.target.checked)} />
                        <span className={(form.roles || []).includes('store') ? '' : 'role-cb--dim'}>Store</span>
                    </label>
                </div>
                {/* Warehouse role — checkbox only, no behavior dropdown */}
                <div className={`role-row ${(form.roles || []).includes('warehouse') ? '' : 'role-row--off'}`}>
                    <label className="role-cb">
                        <input type="checkbox" checked={(form.roles || []).includes('warehouse')}
                            onChange={e => toggleRole('warehouse', e.target.checked)} />
                        <span className={(form.roles || []).includes('warehouse') ? '' : 'role-cb--dim'}>Warehouse</span>
                    </label>
                </div>
                {/* Warehouse extra — whether this PIN may start Cycle Counts. Stock Takes are started by an admin; a PIN only joins. */}
                {WAREHOUSE_V2 && (form.roles || []).includes('warehouse') && [
                    ['warehouse.create_cycle_count', 'Can start Cycle Counts'],
                ].map(([key, label]) => (
                    <div className="role-row" key={key} style={{ paddingLeft: 28 }}>
                        <label className="role-cb">
                            <input type="checkbox" checked={(form.roles || []).includes(key)}
                                onChange={e => toggleRole(key, e.target.checked)} />
                            <span>{label}</span>
                        </label>
                    </div>
                ))}
                <div className={`role-row ${(form.roles || []).includes('bulk_shipping') ? '' : 'role-row--off'}`}>
                    <label className="role-cb">
                        <input type="checkbox" checked={(form.roles || []).includes('bulk_shipping')}
                            onChange={e => toggleRole('bulk_shipping', e.target.checked)} />
                        <span className={(form.roles || []).includes('bulk_shipping') ? '' : 'role-cb--dim'}>Bulk Shipping</span>
                    </label>
                </div>
            </div>
            <div className="role-help">
                "Scan only" is overridden when the product's <code>custom.require_scanning</code> ≠ true.
            </div>
        </div>
    );
}

/**
 * Shared form body used by both Add and Edit portal-user modals.
 */
function PortalUserForm({ form, setForm, isEdit, allLocationsByStore, myLocSet, isStoreManager = false }) {
    return (
        <BlockStack gap="400">
            <div className="settings-grid-2">
                <div>
                    <label className="settings-field-label">First Name</label>
                    <input className="input" value={form.first_name ?? ''} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" />
                </div>
                <div>
                    <label className="settings-field-label">Last Name</label>
                    <input className="input" value={form.last_name ?? ''} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" />
                </div>
            </div>
            <div>
                <label className="settings-field-label">
                    {isEdit ? 'New PIN (leave blank to keep current)' : '4 or 5 Digit PIN'}
                </label>
                <input className="input input--pin"
                    value={form.pin ?? ''}
                    onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                    placeholder={isEdit ? 'New PIN' : 'e.g. 12345'} maxLength={5} />
            </div>
            {/* Locations */}
            <div>
                <label className="settings-field-label">Locations{isEdit ? '' : ' (select at least one)'}</label>
                <ScrollableLocationCheckboxes
                    assignments={form.assignments ?? []}
                    onChange={assignments => setForm(f => ({ ...f, assignments }))}
                    allLocationsByStore={allLocationsByStore || {}}
                    myLocSet={myLocSet}
                />
            </div>
            {/* Roles & Behavior — locked to "Store" for store_manager callers. */}
            {isStoreManager ? (
                <div>
                    <label className="settings-field-label">Role</label>
                    <div style={{
                        border: '1px solid #e4e7eb', borderRadius: 8, padding: '10px 12px',
                        background: '#f0f9ff', color: '#1e3a8a', fontWeight: 500,
                    }}>
                        Store — Store Pickup Portal only
                    </div>
                </div>
            ) : (
                <RolesAndBehavior form={form} setForm={setForm} />
            )}
            {/* Manager — can override scan-only across all portals. Hidden for
                store_manager: their PIN users are 'store' only, so there's no
                scan-only override to authorize. */}
            {!isStoreManager && (
            <div style={{
                border: '1px solid #e4e7eb', borderRadius: 8, padding: '10px 12px',
                background: form.is_manager ? '#f0f9ff' : '#fafbfc',
            }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={!!form.is_manager}
                        onChange={e => setForm(f => ({ ...f, is_manager: e.target.checked }))} />
                    <span><strong>Manager</strong> — can authorize scan-only overrides on any portal</span>
                </label>
                {form.is_manager && form.manager_barcode && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Override barcode
                            </div>
                            <code style={{
                                fontSize: 14, fontFamily: 'monospace',
                                background: '#fff', padding: '4px 8px',
                                borderRadius: 4, border: '1px solid #d1d5db',
                            }}>{form.manager_barcode}</code>
                        </div>
                        <a href={`/settings/manager-barcode/print/${form.id}`}
                           target="_blank" rel="noreferrer"
                           className="btn btn--secondary"
                           style={{ textDecoration: 'none' }}>
                            🖨 Print Barcode
                        </a>
                    </div>
                )}
                {form.is_manager && !form.manager_barcode && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                        Barcode will be generated and shown here after you save.
                    </div>
                )}
            </div>
            )}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={form.active ?? true}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                Active
            </label>
        </BlockStack>
    );
}

function PresserUsersTab() {
    const { user: authUser } = useAuth();
    const isAdmin = authUser?.role === 'admin';
    const canSeePin = isAdmin || authUser?.role === 'franchise' || authUser?.role === 'site_manager';
    const [pressers, setPressers]                   = useState([]);
    const [allLocationsByStore, setAllLocationsByStore] = useState({});
    const [showAdd, setShowAdd]                     = useState(false);
    const [duplicateFromName, setDuplicateFromName] = useState('');
    const initialForm = authUser?.role === 'store_manager' ? EMPTY_STORE_MANAGER_FORM : EMPTY_PRESSER_FORM;
    const [form, setForm]                           = useState(initialForm);
    const [editId, setEditId]                       = useState(null);
    const [editForm, setEditForm]                   = useState({});
    const [loading, setLoading]                     = useState(true);
    const [msg, setMsg]                             = useState(null);
    const [msgError, setMsgError]                   = useState(false);
    const [confirmDelete, setConfirmDelete]         = useState(null); // { id, name }
    const [pinConflict, setPinConflict]             = useState(false);
    // Modal-scoped error so validation messages render INSIDE the Add / Edit
    // Portal User modals instead of behind them on the Settings page banner.
    // Trent hit "Create" with a missing field and saw nothing because the
    // page-level banner was hidden by the modal backdrop.
    const [addError, setAddError]                   = useState(null);
    const [editError, setEditError]                 = useState(null);
    const [nameFilter, setNameFilter]               = useState('');
    // Admin-only location filter at the top of the table. Value is
    // "<store_key>:<shopify_location_id>" or "" for All Locations.
    const [locFilter, setLocFilter]                 = useState('');
    const { sortField, sortDir, onSort, sortRows } = useSortableTable('name');

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const load = () => {
        setLoading(true);
        // site_manager gets the corp-stores expansion so they can add Store-role
        // PIN users at non-franchise retail locations within their store_keys.
        const locsQs = authUser?.role === 'site_manager' ? '&include_corp_stores=true' : '';
        Promise.all([
            authenticatedFetch('/api/portal-users').then(r => r.json()),
            ...STORE_KEYS.map(sk => authenticatedFetch(`/api/locations?store=${sk}${locsQs}`).then(r => r.json())),
        ]).then(([p, ...locArrays]) => {
            setPressers(p);
            const byStore = {};
            STORE_KEYS.forEach((sk, i) => {
                byStore[sk] = (locArrays[i] || []).filter(l => l.active && l.shopify_location_id !== '0');
            });
            setAllLocationsByStore(byStore);
        }).finally(() => setLoading(false));
    };

    useEffect(load, []);

    // Toggle a store+location in an assignments array
    const toggleAssignment = (currentAssignments, store_key, shopify_location_id) => {
        const exists = currentAssignments.some(
            a => a.store_key === store_key && a.shopify_location_id === shopify_location_id
        );
        return exists
            ? currentAssignments.filter(a => !(a.store_key === store_key && a.shopify_location_id === shopify_location_id))
            : [...currentAssignments, { store_key, shopify_location_id }];
    };

    // Resolve assignment display labels from loaded locations, sorted by name.
    // Non-admin (franchise/site_manager): only show assignments at locations the
    // caller themselves has access to — the rest of the user's assignments are
    // outside their scope and showing them would leak info.
    const assignmentLines = (assignments) => {
        if (!assignments || assignments.length === 0) return <span className="text-faint">—</span>;
        let scoped = assignments;
        if (!isAdmin && authUser?.locations) {
            const own = new Set(authUser.locations.map(l => l.shopify_location_id));
            scoped = assignments.filter(a => own.has(a.shopify_location_id));
            if (scoped.length === 0) return <span className="text-faint">—</span>;
        }
        const resolved = scoped.map(a => {
            const locs = allLocationsByStore[a.store_key] || [];
            const loc  = locs.find(l => l.shopify_location_id === a.shopify_location_id);
            return {
                name: loc ? loc.name : String(a.shopify_location_id),
                prefix: STORE_DISPLAY_NAMES[a.store_key] || a.store_key,
            };
        }).sort((x, y) => x.name.localeCompare(y.name));
        return resolved.map((r, i) => <div key={i}>{r.prefix}: {r.name}</div>);
    };

    const handleAdd = async () => {
        setAddError(null);
        if (!form.first_name.trim() || !form.last_name.trim()) {
            return setAddError('First and last name are required.');
        }
        if (!form.assignments || form.assignments.length === 0) return setAddError('At least one location is required.');
        if (!/^\d{4,5}$/.test(form.pin)) return setAddError('PIN must be 4 or 5 digits.');
        if (!form.roles || form.roles.length === 0) {
            return setAddError('At least one role must be selected.');
        }
        const res = await authenticatedFetch('/api/portal-users', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                first_name: form.first_name, last_name: form.last_name, pin: form.pin,
                roles: form.roles, active: form.active, assignments: form.assignments,
                scan_only_picker:  form.scan_only_picker,
                scan_only_presser: form.scan_only_presser,
                scan_only_packer:  form.scan_only_packer,
                is_manager:        !!form.is_manager,
            }),
        });
        if (res.ok) { flash('Portal user created.'); setShowAdd(false); setAddError(null); setForm(initialForm); setDuplicateFromName(''); load(); }
        else if (res.status === 409) { setPinConflict(true); }
        else { const e = await res.json(); setAddError(`Error: ${e.detail}`); }
    };

    const handleUpdate = async (id, currentUser) => {
        setEditError(null);
        if (editForm.pin && !/^\d{4,5}$/.test(editForm.pin)) {
            return setEditError('PIN must be 4 or 5 digits.');
        }
        const body = {};
        if (editForm.first_name !== undefined) body.first_name = editForm.first_name;
        if (editForm.last_name  !== undefined) body.last_name  = editForm.last_name;
        if (editForm.pin)                      body.pin        = editForm.pin;
        if (editForm.active     !== undefined) body.active     = editForm.active;
        if (editForm.roles      !== undefined) body.roles      = editForm.roles;
        if (editForm.assignments !== undefined) body.assignments = editForm.assignments;
        if (editForm.scan_only_picker  !== undefined) body.scan_only_picker  = editForm.scan_only_picker;
        if (editForm.scan_only_presser !== undefined) body.scan_only_presser = editForm.scan_only_presser;
        if (editForm.scan_only_packer  !== undefined) body.scan_only_packer  = editForm.scan_only_packer;
        if (editForm.is_manager        !== undefined) body.is_manager        = !!editForm.is_manager;
        if (!body.pin) delete body.pin;
        const res = await authenticatedFetch(`/api/portal-users/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (res.ok) { flash('Portal user updated.'); setEditId(null); setEditError(null); load(); }
        else if (res.status === 409) { setPinConflict(true); }
        else { const e = await res.json(); setEditError(`Error: ${e.detail}`); }
    };

    const handleDelete = (id, name) => setConfirmDelete({ id, name });

    const confirmDeletePresser = async () => {
        await authenticatedFetch(`/api/portal-users/${confirmDelete.id}`, { method: 'DELETE' });
        setConfirmDelete(null);
        load();
    };

    // Multi-store location checkboxes (used in both add form and edit row).
    // For franchise: restrict to only the user's own locations (client-side filter).
    // For site_manager: backend /api/locations was called with include_corp_stores=true,
    // so allLocationsByStore already contains the expanded set (own + non-franchise
    // retail) — skip the client filter and let everything through.
    const isSiteManager = authUser?.role === 'site_manager';
    const myLocSet = (!isAdmin && !isSiteManager && authUser?.locations)
        ? new Set(authUser.locations.map(l => l.shopify_location_id))
        : null;

    return (
        <>
        {confirmDelete && (
            <DeleteConfirmModal
                title="Delete Portal User"
                message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
                onConfirm={confirmDeletePresser}
                onCancel={() => setConfirmDelete(null)}
            />
        )}
        {pinConflict && (
            <div className="modal-backdrop">
                <div className="modal">
                    <div className="modal__title">PIN Already Taken</div>
                    <div className="modal__body">
                        That PIN is already in use by another user. PINs must be unique across all stores. Please choose a different PIN.
                    </div>
                    <div className="modal__footer">
                        <button className="btn btn--primary" onClick={() => setPinConflict(false)}>Choose a Different PIN</button>
                    </div>
                </div>
            </div>
        )}
        {editId !== null && (() => {
            return (
                <Modal
                    open={true}
                    onClose={() => { setEditId(null); setEditError(null); }}
                    title={`Edit: ${editForm.first_name || ''} ${editForm.last_name || ''}`}
                    large
                    primaryAction={{ content: 'Save', onAction: () => handleUpdate(editId) }}
                    secondaryActions={[{ content: 'Cancel', onAction: () => { setEditId(null); setEditError(null); } }]}
                >
                    {/* Polaris Modal `large` caps at ~620px. Widen via :has() override
                        scoped to the marker class below — keeps other modals untouched. */}
                    <style>{`.Polaris-Modal-Dialog__Modal:has(.presser-edit-modal-marker) { max-width: 880px !important; }`}</style>
                    <Modal.Section>
                        <div className="presser-edit-modal-marker" />
                        {editError && (
                            <Banner tone="critical" onDismiss={() => setEditError(null)}>{editError}</Banner>
                        )}
                        <PortalUserForm form={editForm} setForm={setEditForm} isEdit allLocationsByStore={allLocationsByStore} myLocSet={myLocSet} isStoreManager={authUser?.role === 'store_manager'} />
                    </Modal.Section>
                </Modal>
            );
        })()}
        {showAdd && (
            <Modal
                open={true}
                onClose={() => { setShowAdd(false); setAddError(null); setForm(initialForm); setDuplicateFromName(''); }}
                title={duplicateFromName
                    ? `Add Portal User (duplicating settings from ${duplicateFromName})`
                    : 'Add Portal User'}
                large
                primaryAction={{ content: 'Create', onAction: handleAdd }}
                secondaryActions={[{ content: 'Cancel', onAction: () => { setShowAdd(false); setAddError(null); setForm(initialForm); setDuplicateFromName(''); } }]}
            >
                <Modal.Section>
                    {addError && (
                        <Banner tone="critical" onDismiss={() => setAddError(null)}>{addError}</Banner>
                    )}
                    <PortalUserForm form={form} setForm={setForm} allLocationsByStore={allLocationsByStore} myLocSet={myLocSet} isStoreManager={authUser?.role === 'store_manager'} />
                </Modal.Section>
            </Modal>
        )}
        <BlockStack gap="400">
            {msg && (
                <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>
            )}
            <Card>
                <BlockStack gap="400">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingSm" as="h3">Portal (PIN) Users</Text>
                        <Button variant="primary" onClick={() => setShowAdd(true)}>Add User</Button>
                    </InlineStack>

                    {/* Admin-only location filter. Builds options across every
                        store's active locations, prefixed with store name so
                        admins can scope the table to one location at a time. */}
                    {isAdmin && (() => {
                        const locOptions = [{ label: 'All locations', value: '' }];
                        STORE_KEYS.forEach(sk => {
                            const locs = (allLocationsByStore[sk] || [])
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                            locs.forEach(l => locOptions.push({
                                label: `${STORE_DISPLAY_NAMES[sk] || sk}: ${l.name}`,
                                value: `${sk}:${l.shopify_location_id}`,
                            }));
                        });
                        return (
                            <div style={{ maxWidth: 360 }}>
                                <Select
                                    label="Filter by location"
                                    labelHidden
                                    options={locOptions}
                                    value={locFilter}
                                    onChange={setLocFilter}
                                />
                            </div>
                        );
                    })()}

                    <div style={{ maxWidth: 360 }}>
                        <TextField
                            label="Filter by name"
                            labelHidden
                            placeholder="Filter by name"
                            value={nameFilter}
                            onChange={setNameFilter}
                            clearButton
                            onClearButtonClick={() => setNameFilter('')}
                            autoComplete="off"
                        />
                    </div>

                    {loading ? (
                        <Text tone="subdued">Loading…</Text>
                    ) : (
                        <div className="settings-users-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <SortableHeader label="Name" field="name" sortField={sortField} sortDir={sortDir} onSort={onSort} style={{ maxWidth: '20ch', whiteSpace: 'normal' }} />
                                    <SortableHeader label="Locations" field="locations" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    {canSeePin && <SortableHeader label="PIN" field="pin" sortField={sortField} sortDir={sortDir} onSort={onSort} />}
                                    <SortableHeader label="Roles" field="roles" sortField={sortField} sortDir={sortDir} onSort={onSort} style={{ maxWidth: '32ch', whiteSpace: 'normal' }} />
                                    <SortableHeader label="Language" field="language" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <SortableHeader label="Last login" field="last_login_at" sortField={sortField} sortDir={sortDir} onSort={onSort} />
                                    <th>Status</th>
                                    {isAdmin && <th>Added By</th>}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortRows(
                                    (() => {
                                        let list = locFilter
                                            ? pressers.filter(p => {
                                                const [sk, sloc] = locFilter.split(':');
                                                return (p.assignments || []).some(
                                                    a => a.store_key === sk && String(a.shopify_location_id) === sloc
                                                );
                                            })
                                            : pressers;
                                        const q = nameFilter.trim().toLowerCase();
                                        if (q) {
                                            list = list.filter(p => {
                                                const hay = [
                                                    p.first_name, p.last_name,
                                                    `${p.first_name || ''} ${p.last_name || ''}`,
                                                ].filter(Boolean).join(' ').toLowerCase();
                                                return hay.includes(q);
                                            });
                                        }
                                        return list;
                                    })(),
                                    (p, f) => {
                                    if (f === 'name') return `${p.first_name} ${p.last_name}`;
                                    if (f === 'locations') {
                                        const names = (p.assignments || [])
                                            .map(a => {
                                                const locs = allLocationsByStore[a.store_key] || [];
                                                const loc = locs.find(l => l.shopify_location_id === a.shopify_location_id);
                                                return (loc?.name || '').toLowerCase();
                                            })
                                            .filter(Boolean)
                                            .sort();
                                        return names[0] || '\uffff';
                                    }
                                    if (f === 'pin') return p.pin_plain || '';
                                    if (f === 'roles') return (p.roles || ['presser']).join(', ');
                                    if (f === 'language') return (p.language || 'en') === 'es' ? 'spanish' : 'english';
                                    return p[f];
                                }).map(p => (
                                    <React.Fragment key={p.id}>
                                        <tr>
                                            <td className="td-bold" style={{ maxWidth: '20ch', whiteSpace: 'normal' }}>{p.first_name} {p.last_name}</td>
                                            <td className="td-muted">{assignmentLines(p.assignments)}</td>
                                            {canSeePin && <td className="td-pin">{p.pin_plain || '••••'}</td>}
                                            <td className="td-muted" style={{ maxWidth: '32ch', whiteSpace: 'normal' }}>
                                                {p.is_manager && (
                                                    <>
                                                        <strong>Manager</strong>
                                                        {(p.roles || []).length > 0 && ', '}
                                                    </>
                                                )}
                                                {(p.roles || ['presser']).map(r => {
                                                    const base = ROLE_LABELS[r] || r;
                                                    if (r === 'picker'  && p.scan_only_picker)  return `${base} (Scan)`;
                                                    if (r === 'presser' && p.scan_only_presser) return `${base} (Scan)`;
                                                    if (r === 'packing' && p.scan_only_packer)  return `${base} (Scan)`;
                                                    return base;
                                                }).join(', ')}
                                            </td>
                                            <td className="td-muted">
                                                {/* Editable by admin (any row) or by non-admins for portal users
                                                    they (or other non-admins) created — same gate as the Delete
                                                    button. Site managers can change language on their own setups. */}
                                                {(isAdmin || p.created_by_role !== 'admin') ? (
                                                    <Select labelHidden label="Language" options={[
                                                        { label: 'English', value: 'en' },
                                                        { label: 'Spanish', value: 'es' },
                                                    ]} value={p.language || 'en'} onChange={async v => {
                                                        const res = await authenticatedFetch(`/api/portal-users/${p.id}`, {
                                                            method: 'PUT',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ language: v }),
                                                        });
                                                        if (res.ok) { flash('Language updated.'); load(); }
                                                        else { const e = await res.json().catch(() => ({})); flash(`Error: ${e.detail || 'Could not update language.'}`, true); }
                                                    }} />
                                                ) : (
                                                    (p.language || 'en') === 'es' ? 'Spanish' : 'English'
                                                )}
                                            </td>
                                            <td className="td-muted" title={p.last_login_at || 'Never'}>
                                                {lastUseText(p.last_login_at)}
                                            </td>
                                            <td>
                                                <Badge tone={p.active ? 'success' : undefined}>{p.active ? 'Active' : 'Inactive'}</Badge>
                                            </td>
                                            {isAdmin && (
                                                <td className="td-muted" style={{ fontSize: 12 }}>
                                                    {p.created_by_display ? `${p.created_by_display} (${p.created_by_role || '?'})` : '—'}
                                                </td>
                                            )}
                                            <td>
                                                <InlineStack gap="200" wrap={false}>
                                                    <Button size="slim" onClick={() => {
                                                        setEditId(p.id);
                                                        setEditForm({
                                                            id: p.id,
                                                            first_name: p.first_name,
                                                            last_name: p.last_name,
                                                            pin: '',
                                                            active: p.active,
                                                            roles: p.roles || ['presser'],
                                                            scan_only_picker:  p.scan_only_picker  || false,
                                                            scan_only_presser: p.scan_only_presser || false,
                                                            scan_only_packer:  p.scan_only_packer  || false,
                                                            is_manager:        !!p.is_manager,
                                                            manager_barcode:   p.manager_barcode || null,
                                                            assignments: p.assignments || [],
                                                        });
                                                    }}>Edit</Button>
                                                    {(isAdmin || p.created_by_role !== 'admin') && (
                                                        <Button size="slim" onClick={() => {
                                                            // Open Add modal pre-populated with this user's roles, scan-only
                                                            // flags, active state, and assignments — but blank name + PIN
                                                            // so the operator only fills in identity for the new user.
                                                            //
                                                            // For non-admins we filter the prefilled assignments to only
                                                            // those visible in the caller's own location picker. Otherwise
                                                            // duplicating a cross-store PIN user copies out-of-scope
                                                            // assignments invisibly into form state, and the backend used
                                                            // to accept them silently (SP-329xxx audit).
                                                            const visibleAssignments = (p.assignments || []).filter(a => {
                                                                if (isAdmin) return true;
                                                                // Every location in the current picker set — same list the
                                                                // Locations picker shows the caller.
                                                                const locsInStore = allLocationsByStore[a.store_key] || [];
                                                                const inPicker = locsInStore.some(l =>
                                                                    l.shopify_location_id === a.shopify_location_id);
                                                                if (!inPicker) return false;
                                                                if (!myLocSet) return true;   // admin/site_manager (no client-side filter)
                                                                return myLocSet.has(a.shopify_location_id);
                                                            });
                                                            setEditId(null);
                                                            setDuplicateFromName(`${p.first_name} ${p.last_name}`);
                                                            setForm({
                                                                ...EMPTY_PRESSER_FORM,
                                                                active: p.active,
                                                                roles: p.roles && p.roles.length ? p.roles : ['picker'],
                                                                scan_only_picker:  p.scan_only_picker  || false,
                                                                scan_only_presser: p.scan_only_presser || false,
                                                                scan_only_packer:  p.scan_only_packer  || false,
                                                                assignments: visibleAssignments.map(a => ({
                                                                    store_key: a.store_key,
                                                                    shopify_location_id: a.shopify_location_id,
                                                                })),
                                                            });
                                                            setShowAdd(true);
                                                        }}>Duplicate</Button>
                                                    )}
                                                    {(isAdmin || p.created_by_role !== 'admin') && (
                                                        <Button size="slim" tone="critical" onClick={() => handleDelete(p.id, `${p.first_name} ${p.last_name}`)}>Delete</Button>
                                                    )}
                                                </InlineStack>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                                {pressers.length === 0 && (
                                    <tr><td colSpan={7} className="settings-empty-cell">No portal users yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
        </>
    );
}

// ── Picking Portal settings tab ───────────────────────────────────────────────

function PickingPortalTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlStore = searchParams.get('store');
    const [storeKey, setStoreKey]   = useState(
        urlStore && STORE_KEYS.includes(urlStore) ? urlStore : STORE_KEYS[0]
    );
    useEffect(() => {
        if (urlStore && STORE_KEYS.includes(urlStore) && urlStore !== storeKey) setStoreKey(urlStore);
    }, [urlStore]); // eslint-disable-line
    const setStoreAndUrl = (sk) => {
        setStoreKey(sk);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', 'picking-portal');
            next.set('store', sk);
            return next;
        }, { replace: true });
    };
    const [settings, setSettings]   = useState(null);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [msg, setMsg]             = useState('');
    const [msgError, setMsgError]   = useState(false);
    const [showAdd, setShowAdd]     = useState(false);
    const [addForm, setAddForm]     = useState({ location_id: '', level: 'product', metafield_key: '', kind: 'primary' });
    const [editIdx, setEditIdx]     = useState(null);
    const [editForm, setEditForm]   = useState({});
    // 2026-09-13: Bin Locations Report — per-location bin grid definition modal.
    const [binsFor, setBinsFor]     = useState(null);

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const load = () => {
        setLoading(true);
        Promise.all([
            authenticatedFetch(`/api/picking/settings?store=${storeKey}`).then(r => r.json()),
            authenticatedFetch(`/api/locations?store=${storeKey}`).then(r => r.json()),
        ])
            .then(([s, locs]) => {
                setSettings(s);
                setLocations(locs);
            })
            .catch(() => flash('Failed to load settings.', true))
            .finally(() => setLoading(false));
    };

    useEffect(load, [storeKey]);

    const saveSettings = async (newSettings) => {
        setSaving(true);
        try {
            const res = await authenticatedFetch(`/api/picking/settings?store=${storeKey}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: newSettings }),
            });
            if (res.ok) { setSettings(newSettings); flash('Settings saved.'); }
            else flash('Failed to save.', true);
        } catch { flash('Network error.', true); }
        finally { setSaving(false); }
    };

    // Each (location_id, kind) pair is unique. A location is "available" to add a
    // metafield to as long as at least one of its (primary, overstock) slots is open.
    // Treat missing kind on legacy entries as 'primary'.
    const slotKey = (locId, kind) => `${locId}|${kind || 'primary'}`;
    const filledSlots = new Set(
        (settings?.location_metafields || []).map(m => slotKey(m.location_id, m.kind))
    );
    const availableLocations = locations.filter(l =>
        !(filledSlots.has(slotKey(l.id, 'primary')) && filledSlots.has(slotKey(l.id, 'overstock')))
    );
    const availableKindsFor = (locId) => {
        const id = parseInt(locId, 10);
        const opts = [];
        if (!filledSlots.has(slotKey(id, 'primary')))   opts.push({ label: 'Primary',   value: 'primary'   });
        if (!filledSlots.has(slotKey(id, 'overstock'))) opts.push({ label: 'Overstock', value: 'overstock' });
        return opts;
    };

    const handleAddSave = () => {
        if (!addForm.location_id || !addForm.metafield_key.trim()) return flash('Fill in all fields.', true);
        const newMf = {
            location_id:   parseInt(addForm.location_id, 10),
            level:         addForm.level,
            metafield_key: addForm.metafield_key.trim(),
            kind:          addForm.kind || 'primary',
        };
        if (filledSlots.has(slotKey(newMf.location_id, newMf.kind))) {
            return flash(`A ${newMf.kind} metafield already exists for this location.`, true);
        }
        const newSettings = { ...settings, location_metafields: [...(settings.location_metafields || []), newMf] };
        saveSettings(newSettings);
        setShowAdd(false);
        setAddForm({ location_id: '', level: 'product', metafield_key: '', kind: 'primary' });
    };

    const handleDelete = (idx) => {
        const mfs = [...(settings.location_metafields || [])];
        mfs.splice(idx, 1);
        saveSettings({ ...settings, location_metafields: mfs });
    };

    const handleEditSave = (idx) => {
        const mfs = [...(settings.location_metafields || [])];
        mfs[idx] = { ...mfs[idx], ...editForm };
        saveSettings({ ...settings, location_metafields: mfs });
        setEditIdx(null);
    };

    if (loading) return <Text>Loading…</Text>;

    return (
        <StoreSubTabsPanel storeKey={storeKey} onStoreChange={setStoreAndUrl}>
        <BlockStack gap="400">
            {msg && <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg('')}>{msg}</Banner>}


            {/* Location Metafields */}
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between">
                        <Text variant="headingMd">Location Metafields</Text>
                        {availableLocations.length > 0 && !showAdd && (
                            <Button onClick={() => setShowAdd(true)} size="slim">+ Add</Button>
                        )}
                    </InlineStack>
                    <Text variant="bodySm" tone="subdued">
                        Configure which Shopify metafield to show as the bin/shelf location for each picking location.
                    </Text>

                    <BinDefinitionModal open={!!binsFor} onClose={() => setBinsFor(null)} storeKey={storeKey}
                                        locationId={binsFor?.id} locationName={binsFor?.name}
                                        onSaved={() => authenticatedFetch(`/api/picking/settings?store=${storeKey}`).then(r => r.json()).then(setSettings)} />
                    {(settings?.location_metafields || []).length === 0 && !showAdd && (
                        <Text tone="subdued">No location metafields configured.</Text>
                    )}

                    {(settings?.location_metafields || []).map((mf, idx) => {
                        const loc = locations.find(l => l.id === mf.location_id);
                        const kind = (mf.kind || 'primary');
                        const isOverstock = kind === 'overstock';
                        if (editIdx === idx) {
                            const editKind = editForm.kind || 'primary';
                            return (
                                <div key={idx} style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                                    <BlockStack gap="200">
                                        <BlockStack gap="100">
                                            <Text variant="bodyMd" as="span">Kind</Text>
                                            <ButtonGroup variant="segmented">
                                                <Button
                                                    pressed={editKind === 'primary'}
                                                    onClick={() => setEditForm(f => ({ ...f, kind: 'primary' }))}>
                                                    Primary
                                                </Button>
                                                <Button
                                                    pressed={editKind === 'overstock'}
                                                    onClick={() => setEditForm(f => ({ ...f, kind: 'overstock' }))}>
                                                    Overstock
                                                </Button>
                                            </ButtonGroup>
                                            <Text variant="bodySm" tone="subdued">
                                                Primary = the picker's pick-face bin. Overstock = the refill / backup location (used in printed orders).
                                            </Text>
                                        </BlockStack>
                                        <BlockStack gap="100">
                                            <Text variant="bodyMd" as="span">Level</Text>
                                            <ButtonGroup variant="segmented">
                                                <Button
                                                    pressed={(editForm.level || 'product') === 'product'}
                                                    onClick={() => setEditForm(f => ({ ...f, level: 'product' }))}>
                                                    Product
                                                </Button>
                                                <Button
                                                    pressed={editForm.level === 'variant'}
                                                    onClick={() => setEditForm(f => ({ ...f, level: 'variant' }))}>
                                                    Variant
                                                </Button>
                                            </ButtonGroup>
                                        </BlockStack>
                                        <TextField
                                            label="Metafield Key"
                                            value={editForm.metafield_key}
                                            onChange={v => setEditForm(f => ({ ...f, metafield_key: v }))}
                                            autoComplete="off"
                                            placeholder="custom.location_teams_brookfield"
                                        />
                                        <InlineStack gap="200">
                                            <Button size="slim" onClick={() => handleEditSave(idx)} loading={saving}>Save</Button>
                                            <Button size="slim" onClick={() => setEditIdx(null)}>Cancel</Button>
                                        </InlineStack>
                                    </BlockStack>
                                </div>
                            );
                        }
                        return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f1f1' }}>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: 12,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.3,
                                        background: isOverstock ? '#fef3c7' : '#dbeafe',
                                        color:      isOverstock ? '#92400e' : '#1e40af',
                                        minWidth: 70,
                                        textAlign: 'center',
                                    }}>{isOverstock ? 'Overstock' : 'Primary'}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{loc?.name || `Location #${mf.location_id}`}</div>
                                        <div style={{ fontSize: 12, color: '#6d7175' }}>
                                            {mf.level === 'product' ? 'Product' : 'Variant'} · <code>{mf.metafield_key}</code>
                                        </div>
                                    </div>
                                </div>
                                {kind === 'primary' && (
                                    <Button size="slim" onClick={() => setBinsFor({ id: mf.location_id, name: loc?.name || `Location #${mf.location_id}` })}>
                                        Define bins…{mf.bins?.grid?.length ? ` (${mf.bins.grid.length} range${mf.bins.grid.length === 1 ? '' : 's'})` : ''}
                                    </Button>
                                )}
                                <Button size="slim" onClick={() => { setEditIdx(idx); setEditForm({ level: mf.level, metafield_key: mf.metafield_key, kind }); }}>Edit</Button>
                                <Button size="slim" tone="critical" onClick={() => handleDelete(idx)}>Delete</Button>
                            </div>
                        );
                    })}

                    {showAdd && (() => {
                        const kindOpts = addForm.location_id ? availableKindsFor(addForm.location_id) : [
                            { label: 'Primary',   value: 'primary'   },
                            { label: 'Overstock', value: 'overstock' },
                        ];
                        // If the currently-selected kind is no longer available for the chosen
                        // location, snap to the first remaining option.
                        if (kindOpts.length > 0 && !kindOpts.some(o => o.value === addForm.kind)) {
                            setTimeout(() => setAddForm(f => ({ ...f, kind: kindOpts[0].value })), 0);
                        }
                        return (
                        <div style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            <BlockStack gap="200">
                                <Select
                                    label="Location"
                                    options={[{ label: '— Select location —', value: '' }, ...availableLocations.map(l => ({ label: l.name, value: String(l.id) }))]}
                                    value={addForm.location_id}
                                    onChange={v => setAddForm(f => ({ ...f, location_id: v }))}
                                />
                                <BlockStack gap="100">
                                    <Text variant="bodyMd" as="span">Kind</Text>
                                    <ButtonGroup variant="segmented">
                                        <Button
                                            pressed={addForm.kind === 'primary'}
                                            disabled={!kindOpts.some(o => o.value === 'primary')}
                                            onClick={() => setAddForm(f => ({ ...f, kind: 'primary' }))}>
                                            Primary
                                        </Button>
                                        <Button
                                            pressed={addForm.kind === 'overstock'}
                                            disabled={!kindOpts.some(o => o.value === 'overstock')}
                                            onClick={() => setAddForm(f => ({ ...f, kind: 'overstock' }))}>
                                            Overstock
                                        </Button>
                                    </ButtonGroup>
                                    <Text variant="bodySm" tone="subdued">
                                        {kindOpts.length === 0
                                            ? 'Both slots already filled for this location.'
                                            : "Primary = the picker's pick-face bin. Overstock = the refill / backup location (used in printed orders)."}
                                    </Text>
                                </BlockStack>
                                <BlockStack gap="100">
                                    <Text variant="bodyMd" as="span">Level</Text>
                                    <ButtonGroup variant="segmented">
                                        <Button
                                            pressed={(addForm.level || 'product') === 'product'}
                                            onClick={() => setAddForm(f => ({ ...f, level: 'product' }))}>
                                            Product
                                        </Button>
                                        <Button
                                            pressed={addForm.level === 'variant'}
                                            onClick={() => setAddForm(f => ({ ...f, level: 'variant' }))}>
                                            Variant
                                        </Button>
                                    </ButtonGroup>
                                </BlockStack>
                                <TextField
                                    label="Metafield Key"
                                    value={addForm.metafield_key}
                                    onChange={v => setAddForm(f => ({ ...f, metafield_key: v }))}
                                    autoComplete="off"
                                    placeholder="custom.location_teams_brookfield"
                                    helpText="Full key including namespace (e.g. custom.location_teams_brookfield)"
                                />
                                <InlineStack gap="200">
                                    <Button size="slim" onClick={handleAddSave} loading={saving} disabled={kindOpts.length === 0}>Save</Button>
                                    <Button size="slim" onClick={() => setShowAdd(false)}>Cancel</Button>
                                </InlineStack>
                            </BlockStack>
                        </div>
                        );
                    })()}
                </BlockStack>
            </Card>

            {/* Debag confirmation */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd">Debag confirmation</Text>
                    <Text variant="bodySm" tone="subdued">
                        Pickers at the checked locations must confirm they have de-bagged each bundle
                        base item (jersey, shorts…) that has embellishments, so it reaches pressing
                        ready to press. Team Portal orders only.
                    </Text>
                    <BlockStack gap="200">
                        {[...locations]
                            .filter(l => l.active)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map(l => (
                                <Checkbox
                                    key={l.id}
                                    label={l.name}
                                    checked={(settings?.debag_locations || []).includes(l.shopify_location_id)}
                                    onChange={(checked) => {
                                        const current = settings.debag_locations || [];
                                        const next = checked
                                            ? [...current, l.shopify_location_id]
                                            : current.filter(id => id !== l.shopify_location_id);
                                        saveSettings({ ...settings, debag_locations: next });
                                    }}
                                />
                            ))}
                    </BlockStack>
                </BlockStack>
            </Card>
        </BlockStack>
        </StoreSubTabsPanel>
    );
}

// ── Advanced settings tab ──────────────────────────────────────────────────────

function AdvancedTab() {
    const [msg, setMsg]             = useState(null);
    const [msgError, setMsgError]   = useState(false);
    const [recognizeOldTags, setRecognizeOldTags] = useState(true);
    const [jumbledIgnoreTeamatical, setJumbledIgnoreTeamatical] = useState(false); // 2026-09-25 (Eric): Advanced Configuration → Jumbled Bundles
    const [teamaticalAutoFulfill, setTeamaticalAutoFulfill] = useState(false);
    const [upsClientId, setUpsClientId]           = useState('');
    const [upsClientSecret, setUpsClientSecret]   = useState('');
    const [upsSecretIsSet, setUpsSecretIsSet]     = useState(false);
    const [upsSaving, setUpsSaving]               = useState(false);

    useEffect(() => {
        authenticatedFetch('/api/advanced-settings')
            .then(r => r.json())
            .then(d => {
                setRecognizeOldTags(d.recognize_old_print_tags ?? true);
                setJumbledIgnoreTeamatical(d.jumbled_ignore_teamatical ?? false);
                setTeamaticalAutoFulfill(d.teamatical_auto_fulfill ?? false);
                setUpsClientId(d.ups_client_id ?? '');
                setUpsSecretIsSet((d.ups_client_secret || '') === '••••');
            })
            .catch(() => {});
    }, []); // eslint-disable-line

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    const saveUpsSettings = async () => {
        setUpsSaving(true);
        try {
            // Blank secret field with an already-set secret means "leave it unchanged" —
            // sending back the mask tells the server to skip overwriting it.
            const secretToSend = (upsClientSecret === '' && upsSecretIsSet) ? '••••' : upsClientSecret;
            const r = await authenticatedFetch('/api/advanced-settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ups_client_id: upsClientId, ups_client_secret: secretToSend }),
            });
            if (!r.ok) { flash('Failed to save UPS settings', true); return; }
            if (upsClientSecret !== '') { setUpsSecretIsSet(true); setUpsClientSecret(''); }
            flash('UPS settings saved.');
        } finally {
            setUpsSaving(false);
        }
    };

    const [tokens, setTokens]                 = useState([]);
    const [tokensLoading, setTokensLoading]   = useState(true);
    const [showRevokedTokens, setShowRevokedTokens] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [tokenForm, setTokenForm]           = useState({ name: '', purpose: '', can_delete_users: false });
    const [tokenCreating, setTokenCreating]   = useState(false);
    const [newTokenPlain, setNewTokenPlain]   = useState(null);
    const [renameTarget, setRenameTarget]     = useState(null);
    const [renameForm, setRenameForm]         = useState({ name: '', purpose: '' });
    const [revokeTarget, setRevokeTarget]     = useState(null);

    const refreshTokens = async () => {
        setTokensLoading(true);
        try {
            const url = showRevokedTokens
                ? '/api/admin/api-tokens?include_revoked=true'
                : '/api/admin/api-tokens';
            const r = await authenticatedFetch(url);
            const d = await r.json();
            setTokens(Array.isArray(d) ? d : []);
        } catch (_) {
            setTokens([]);
        } finally {
            setTokensLoading(false);
        }
    };

    useEffect(() => { refreshTokens(); }, [showRevokedTokens]); // eslint-disable-line

    return (
        <BlockStack gap="400">
            {msg && (
                <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>
            )}
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                            <Text variant="headingMd" as="h2">API Tokens</Text>
                            <Text tone="subdued">
                                Bearer tokens for external integrations (Gorgias, etc.).
                                Tokens cannot be retrieved after creation.
                            </Text>
                        </BlockStack>
                        <Button primary onClick={() => setShowTokenModal(true)}>+ Add Token</Button>
                    </InlineStack>

                    {tokensLoading ? (
                        <Text tone="subdued">Loading…</Text>
                    ) : tokens.length === 0 ? (
                        <Text tone="subdued">No tokens yet.</Text>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                                    <th style={{ padding: '6px 8px' }}>Name</th>
                                    <th style={{ padding: '6px 8px' }}>Purpose</th>
                                    <th style={{ padding: '6px 8px' }}>Delete users</th>
                                    <th style={{ padding: '6px 8px' }}>Token</th>
                                    <th style={{ padding: '6px 8px' }}>Created</th>
                                    <th style={{ padding: '6px 8px' }}>Last used</th>
                                    <th style={{ padding: '6px 8px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokens.map(t => (
                                    <tr key={t.id} style={{
                                        borderBottom: '1px solid #f3f4f6',
                                        color: t.revoked_at ? '#9ca3af' : 'inherit',
                                        fontStyle: t.revoked_at ? 'italic' : 'normal'
                                    }}>
                                        <td style={{ padding: '6px 8px' }}>{t.name}</td>
                                        <td style={{ padding: '6px 8px' }}>{t.purpose || '—'}</td>
                                        {/* 2026-09-22 (Eric): grants this token the external API's
                                            DELETE /users/{email} and /portal-users/{First_Name Last_Name}. */}
                                        <td style={{ padding: '6px 8px' }}>
                                            {t.revoked_at ? (t.can_delete_users ? 'Yes' : '—') : (
                                                <Checkbox
                                                    label="Can delete users"
                                                    labelHidden
                                                    checked={!!t.can_delete_users}
                                                    onChange={async (v) => {
                                                        const r = await authenticatedFetch(`/api/admin/api-tokens/${t.id}`, {
                                                            method: 'PATCH',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ can_delete_users: v }),
                                                        });
                                                        if (!r.ok) { flash('Could not change that permission', true); return; }
                                                        flash(v ? `"${t.name}" can now delete users` : `"${t.name}" can no longer delete users`);
                                                        refreshTokens();
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{t.token_prefix}…</td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}<br/>
                                            <span style={{ color: '#6b7280' }}>{t.created_by}</span>
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'Never'}<br/>
                                            {t.last_used_ip && <span style={{ color: '#6b7280' }}>{t.last_used_ip}</span>}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {!t.revoked_at && (
                                                <InlineStack gap="100">
                                                    <Button size="slim" onClick={() => {
                                                        setRenameTarget(t);
                                                        setRenameForm({ name: t.name, purpose: t.purpose || '' });
                                                    }}>Rename</Button>
                                                    <Button size="slim" tone="critical"
                                                            onClick={() => setRevokeTarget(t)}>Revoke</Button>
                                                </InlineStack>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <Checkbox
                        label="Show revoked tokens"
                        checked={showRevokedTokens}
                        onChange={setShowRevokedTokens}
                    />
                </BlockStack>
            </Card>

            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h2">Advanced Configuration</Text>
                    <Text variant="headingSm" as="h3">Shopify Printed Tags</Text>
                    <Text tone="subdued">
                        Operations Hub uses these tags on Shopify orders: <strong>OpsHubPrinted</strong> (when printed),
                        <strong> OpsHubPicked</strong> (when fully picked), <strong>OpsHubPacked</strong> (when fully packed).
                    </Text>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={recognizeOldTags}
                            onChange={async (e) => {
                                const val = e.target.checked;
                                setRecognizeOldTags(val);
                                await authenticatedFetch('/api/advanced-settings', {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ recognize_old_print_tags: val }),
                                });
                                flash(val ? 'Old print tags will be recognized.' : 'Only OpsHubPrinted will be recognized.');
                            }}
                        />
                        Also recognize legacy print tags ("order printed", "osp: order printed")
                    </label>
                    <Text variant="headingSm" as="h3">Teamatical Bundles</Text>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={jumbledIgnoreTeamatical}
                            onChange={async (e) => {
                                const val = e.target.checked;
                                setJumbledIgnoreTeamatical(val);
                                await authenticatedFetch('/api/advanced-settings', {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ jumbled_ignore_teamatical: val }),
                                });
                                flash(val ? 'Teamatical products are ignored in Jumbled Bundles.' : 'Teamatical products count in Jumbled Bundles again.');
                            }}
                        />
                        <span>
                            Ignore Teamatical products in Jumbled Bundles
                            <div style={{ fontSize: 12, color: '#6d7175' }}>
                                A bundle whose product has <code>Teamatical_v1.teamaticalID</code> is split by design (the garment ships from
                                Teamatical) — it will not show as jumbled on the order, in the Jumbled Bundles report, or in Split Team Fulfillment.
                            </div>
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 24,
                                    cursor: jumbledIgnoreTeamatical ? 'pointer' : 'not-allowed', opacity: jumbledIgnoreTeamatical ? 1 : 0.5 }}>
                        <input
                            type="checkbox"
                            checked={teamaticalAutoFulfill}
                            disabled={!jumbledIgnoreTeamatical}
                            onChange={async (e) => {
                                const val = e.target.checked;
                                setTeamaticalAutoFulfill(val);
                                await authenticatedFetch('/api/advanced-settings', {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ teamatical_auto_fulfill: val }),
                                });
                                flash(val ? 'The rest of a Teamatical bundle will be fulfilled automatically.' : 'Teamatical bundle lines are no longer auto-fulfilled.');
                            }}
                        />
                        <span>
                            Auto-fulfill the rest of a Teamatical bundle
                            <div style={{ fontSize: 12, color: '#6d7175' }}>
                                When the order arrives, every line in the bundle except the garment (number and name subs, add-ons) is fulfilled in
                                Shopify at its own location, without emailing the customer. Recorded on the order timeline.
                            </div>
                        </span>
                    </label>
                </BlockStack>
            </Card>

            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h2">UPS Tracking API</Text>
                    <TextField
                        label="UPS Client ID"
                        value={upsClientId}
                        onChange={setUpsClientId}
                        autoComplete="off"
                    />
                    <TextField
                        label="UPS Client Secret"
                        type="password"
                        value={upsClientSecret}
                        onChange={setUpsClientSecret}
                        placeholder={upsSecretIsSet ? 'unchanged' : ''}
                        autoComplete="off"
                    />
                    <Text tone="subdued">
                        Used by the Bulk Transfers report to poll UPS tracking every 2 hours.
                        Create the app at developer.ups.com under the Soccer Post UPS account.
                    </Text>
                    <Box>
                        <Button onClick={saveUpsSettings} loading={upsSaving}>Save</Button>
                    </Box>
                </BlockStack>
            </Card>


            {/* Add Token modal */}
            <Modal
                open={showTokenModal}
                onClose={() => { setShowTokenModal(false); setNewTokenPlain(null); setTokenForm({ name: '', purpose: '', can_delete_users: false }); }}
                title={newTokenPlain ? "Save this token now" : "Add API Token"}
                primaryAction={newTokenPlain
                    ? { content: 'Done', onAction: () => { setShowTokenModal(false); setNewTokenPlain(null); setTokenForm({ name: '', purpose: '', can_delete_users: false }); refreshTokens(); } }
                    : { content: 'Create token', loading: tokenCreating, onAction: async () => {
                          if (!tokenForm.name.trim()) { flash('Name is required', true); return; }
                          setTokenCreating(true);
                          try {
                              const r = await authenticatedFetch('/api/admin/api-tokens', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ name: tokenForm.name.trim(), purpose: tokenForm.purpose.trim(),
                                                          can_delete_users: !!tokenForm.can_delete_users }),
                              });
                              if (!r.ok) { flash('Failed to create token', true); return; }
                              const d = await r.json();
                              setNewTokenPlain(d.token);
                          } finally { setTokenCreating(false); }
                      } }
                }
                secondaryActions={newTokenPlain ? [] : [{ content: 'Cancel', onAction: () => setShowTokenModal(false) }]}
            >
                <Modal.Section>
                    {newTokenPlain ? (
                        <BlockStack gap="300">
                            <Banner tone="critical">
                                This is the only time the full token will be shown. Copy it now and store it somewhere safe.
                            </Banner>
                            <div style={{ fontFamily: 'monospace', wordBreak: 'break-all', padding: 12, background: '#f3f4f6', borderRadius: 6 }}>
                                {newTokenPlain}
                            </div>
                            <Button onClick={() => navigator.clipboard.writeText(newTokenPlain)}>Copy to clipboard</Button>
                        </BlockStack>
                    ) : (
                        <BlockStack gap="300">
                            <TextField label="Name" value={tokenForm.name}
                                       onChange={v => setTokenForm(f => ({ ...f, name: v }))}
                                       autoComplete="off"
                                       placeholder="Gorgias prod" />
                            <TextField label="Purpose (optional)" value={tokenForm.purpose}
                                       onChange={v => setTokenForm(f => ({ ...f, purpose: v }))}
                                       autoComplete="off"
                                       multiline={2}
                                       placeholder="Customer-service status replies" />
                            <Checkbox
                                label="Allow this token to delete users"
                                helpText="Lets it call DELETE /users/{email} and DELETE /portal-users/{First_Name Last_Name} on the external API. Leave off for a token that only reads order status."
                                checked={!!tokenForm.can_delete_users}
                                onChange={v => setTokenForm(f => ({ ...f, can_delete_users: v }))}
                            />
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>

            {/* Rename modal */}
            <Modal
                open={!!renameTarget}
                onClose={() => setRenameTarget(null)}
                title={renameTarget ? `Rename "${renameTarget.name}"` : 'Rename'}
                primaryAction={{ content: 'Save', onAction: async () => {
                    if (!renameForm.name.trim()) { flash('Name is required', true); return; }
                    const r = await authenticatedFetch(`/api/admin/api-tokens/${renameTarget.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: renameForm.name.trim(), purpose: renameForm.purpose.trim() }),
                    });
                    if (!r.ok) { flash('Rename failed', true); return; }
                    setRenameTarget(null);
                    refreshTokens();
                } }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setRenameTarget(null) }]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <TextField label="Name" value={renameForm.name}
                                   onChange={v => setRenameForm(f => ({ ...f, name: v }))} autoComplete="off" />
                        <TextField label="Purpose" value={renameForm.purpose}
                                   onChange={v => setRenameForm(f => ({ ...f, purpose: v }))} autoComplete="off" multiline={2} />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {/* Revoke confirmation */}
            <Modal
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                title="Revoke token?"
                primaryAction={{ content: 'Revoke token', destructive: true, onAction: async () => {
                    const r = await authenticatedFetch(`/api/admin/api-tokens/${revokeTarget.id}`, { method: 'DELETE' });
                    if (!r.ok) { flash('Revoke failed', true); return; }
                    setRevokeTarget(null);
                    refreshTokens();
                } }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setRevokeTarget(null) }]}
            >
                <Modal.Section>
                    <Text>
                        Revoke <strong>{revokeTarget?.name}</strong>? Any integration using it
                        will start getting 401 immediately. This cannot be undone.
                    </Text>
                </Modal.Section>
            </Modal>

        </BlockStack>
    );
}

// ── Bulk Shipping Rules tab (2026-09-01, TEST-flagged like bulk labels) ──────
// Line-by-line rulebook for the Bulk Print Labels modal: priority-ordered,
// first match wins; each rule can pin a package, a ship-via service, and a
// per-item weight. Backed by /api/settings/bulk-shipping-rules.
const BSR_FIELDS = [
    { label: 'SKU',              value: 'sku',           kind: 'text' },
    { label: 'Product title',    value: 'title',         kind: 'text' },
    { label: 'Product type',     value: 'product_type',  kind: 'text' },
    { label: 'Product tag',      value: 'tag',           kind: 'tag'  },
    { label: 'Vendor',           value: 'vendor',        kind: 'text' },
    { label: 'Variant text',     value: 'variant_title', kind: 'text' },
    { label: 'Size (parsed)',    value: 'size',          kind: 'num'  },
    { label: 'Item weight (lb)', value: 'weight_lb',     kind: 'num'  },
];
const BSR_OPS = {
    text: [
        { label: 'contains any of',    value: 'contains_any' },
        { label: 'is any of',          value: 'equals_any' },
        { label: 'starts with any of', value: 'starts_with_any' },
        { label: 'contains none of',   value: 'not_contains' },
    ],
    tag: [
        { label: 'includes any of',    value: 'contains_any' },
        { label: 'is exactly any of',  value: 'equals_any' },
        { label: 'includes none of',   value: 'not_contains' },
    ],
    num: [
        { label: '≤', value: 'lte' },
        { label: '≥', value: 'gte' },
        { label: 'is any of', value: 'equals_any' },
    ],
};
const BSR_SCOPES = [
    { label: 'EVERY item matches all conditions', value: 'all_items' },
    { label: 'ANY item matches all conditions',   value: 'any_item' },
    { label: 'Always (fallback — keep last)',     value: 'always' },
];
const BSR_SHIP_VIA = [
    { label: '— no preference (UPS Ground default) —', value: '' },
    { label: 'UPS Ground',            value: 'ups_shipping:03' },
    { label: 'UPS Ground Saver (any weight)', value: 'ups_shipping:92' },
    { label: 'UPS 3 Day Select',      value: 'ups_shipping:12' },
    { label: 'UPS 2nd Day Air',       value: 'ups_shipping:02' },
    { label: 'UPS Next Day Air Saver', value: 'ups_shipping:13' },
    { label: 'USPS Ground Advantage', value: 'usps:GroundAdvantage' },
    { label: 'USPS Priority Mail',    value: 'usps:Priority' },
    { label: 'USPS Priority Express', value: 'usps:PriorityExpress' },
];

function BulkShippingRulesTab() {
    const [rules, setRules] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await authenticatedFetch('/api/settings/bulk-shipping-rules');
            const d = await r.json();
            if (!r.ok) throw new Error(d?.detail || `Load failed (${r.status})`);
            setRules((d.rules || []).map((x, i) => ({
                ...x,
                _key: x.id ?? `new-${i}`,
                scope: x.scope || 'all_items',
                conditions: Array.isArray(x.conditions_json) ? x.conditions_json : [],
                _dirty: false,
            })));
            setPackages(d.packages || []);
        } catch (e) { setErr(String(e?.message || e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const patch = (key, p) => setRules(prev => prev.map(r => r._key === key
        ? { ...r, ...(typeof p === 'function' ? p(r) : p), _dirty: true } : r));

    const shipViaValue = (r) => (r.carrier_code && r.service_code) ? `${r.carrier_code}:${r.service_code}` : '';

    const saveRule = async (rule) => {
        setSavingKey(rule._key); setErr(null);
        try {
            const [cc, sc] = (rule._ship_via ?? shipViaValue(rule)).split(':');
            const r = await authenticatedFetch('/api/settings/bulk-shipping-rules', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: rule.id || null,
                    priority: Number(rule.priority) || 100,
                    enabled: !!rule.enabled,
                    name: rule.name || '(unnamed rule)',
                    match_type: 'conditions',
                    scope: rule.scope || 'all_items',
                    conditions: rule.scope === 'always' ? [] : (rule.conditions || []),
                    package_key: rule.package_key || null,
                    carrier_code: cc || null,
                    service_code: sc || null,
                    item_weight_lb: rule.item_weight_lb ? Number(rule.item_weight_lb) : null,
                    notes: rule.notes || null,
                }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d?.detail || `Save failed (${r.status})`);
            await load();
        } catch (e) { setErr(String(e?.message || e)); }
        setSavingKey(null);
    };

    const deleteRule = async (rule) => {
        if (!rule.id) { setRules(prev => prev.filter(r => r._key !== rule._key)); return; }
        if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
        try {
            const r = await authenticatedFetch(`/api/settings/bulk-shipping-rules/${rule.id}`, { method: 'DELETE' });
            if (!r.ok) throw new Error(`Delete failed (${r.status})`);
            await load();
        } catch (e) { setErr(String(e?.message || e)); }
    };

    // Drag-and-drop reorder: on drop, renumber every rule 10/20/30… in the
    // new order and persist the ones whose priority changed.
    const dragIdxRef = useRef(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const onDropReorder = async (fromIdx, toIdx) => {
        setDragOverIdx(null);
        if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
        const next = [...rules];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        const renumbered = next.map((r, i) => ({ ...r, priority: (i + 1) * 10 }));
        setRules(renumbered.map(r => ({ ...r })));
        setSavingKey('reorder'); setErr(null);
        try {
            for (let i = 0; i < renumbered.length; i++) {
                const orig = rules.find(x => x._key === renumbered[i]._key);
                if (renumbered[i].id && orig && Number(orig.priority) !== renumbered[i].priority) {
                    const rr = renumbered[i];
                    const [cc, sc] = (rr._ship_via ?? ((rr.carrier_code && rr.service_code) ? `${rr.carrier_code}:${rr.service_code}` : '')).split(':');
                    const resp = await authenticatedFetch('/api/settings/bulk-shipping-rules', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: rr.id, priority: rr.priority, enabled: !!rr.enabled,
                            name: rr.name || '(unnamed rule)', match_type: 'conditions',
                            scope: rr.scope || 'all_items',
                            conditions: rr.scope === 'always' ? [] : (rr.conditions || []),
                            package_key: rr.package_key || null,
                            carrier_code: cc || null, service_code: sc || null,
                            item_weight_lb: rr.item_weight_lb ? Number(rr.item_weight_lb) : null,
                            notes: rr.notes || null,
                        }),
                    });
                    if (!resp.ok) throw new Error(`Reorder save failed (${resp.status})`);
                }
            }
            await load();
        } catch (e) { setErr(String(e?.message || e)); }
        setSavingKey(null);
    };

    const addRule = () => setRules(prev => [...prev, {
        id: null, _key: `new-${Date.now()}`,
        priority: (prev.length ? Math.max(...prev.map(r => Number(r.priority) || 0)) : 0) + 10,
        enabled: true, name: '', scope: 'all_items',
        conditions: [{ field: 'sku', op: 'starts_with_any', value: '' }],
        package_key: '', carrier_code: null, service_code: null,
        item_weight_lb: '', notes: '', _dirty: true,
    }]);

    const condPatch = (key, ci, p) => patch(key, r => ({
        conditions: r.conditions.map((c, i) => i === ci ? { ...c, ...p } : c),
    }));
    const condAdd = (key) => patch(key, r => ({
        conditions: [...(r.conditions || []), { field: 'sku', op: 'contains_any', value: '' }],
    }));
    const condRemove = (key, ci) => patch(key, r => ({
        conditions: r.conditions.filter((_, i) => i !== ci),
    }));

    const inputS = { padding: '3px 6px', border: '1px solid #c9cccf', borderRadius: 6, fontSize: 12.5 };

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h2">Bulk Shipping Rules</Text>
                        <Button onClick={addRule}>Add rule</Button>
                    </InlineStack>
                    <Text tone="subdued" as="p">
                        The rulebook behind Bulk Print Labels. Rules run top to bottom and the
                        <b> first match wins</b> for each order — a match can pin the package,
                        the ship-via service, and a per-item weight. Build conditions from any
                        product attribute; e.g. the youth-shoe rule is
                        “Product type contains any of <i>shoe, cleat, boot…</i>” + “Size ≤ 6”
                        (youth sizes like YS/3Y parse as ≤ 6). Values accept comma-separated
                        lists. Orders matching no rule get the store-default package and UPS Ground.
                    </Text>
                    {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
                    {loading ? <Text as="p">Loading…</Text> : (
                        <BlockStack gap="300">
                            {rules.map((r, idx) => (
                                <div key={r._key}
                                     onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                                     onDragLeave={() => setDragOverIdx(v => (v === idx ? null : v))}
                                     onDrop={e => { e.preventDefault(); onDropReorder(dragIdxRef.current, idx); dragIdxRef.current = null; }}
                                     style={{ border: dragOverIdx === idx ? '2px dashed #2c6ecb' : '1px solid #e1e3e5',
                                              borderRadius: 10, padding: '10px 14px',
                                              background: r.enabled ? '#fff' : '#fafbfb', opacity: r.enabled ? 1 : 0.6 }}>
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span
                                            draggable
                                            onDragStart={e => { dragIdxRef.current = idx; e.dataTransfer.effectAllowed = 'move'; }}
                                            onDragEnd={() => { dragIdxRef.current = null; setDragOverIdx(null); }}
                                            title="Drag to reorder — rules run top to bottom, first match wins"
                                            style={{ cursor: 'grab', color: '#8c9196', fontSize: 16, lineHeight: 1,
                                                     padding: '2px 6px', userSelect: 'none' }}>⠿</span>
                                        <label style={{ fontSize: 12.5 }}>
                                            <input type="checkbox" checked={!!r.enabled} onChange={e => patch(r._key, { enabled: e.target.checked })} /> on
                                        </label>
                                        <input style={{ ...inputS, fontWeight: 600, minWidth: 260, flex: '1 1 260px' }}
                                               value={r.name || ''} placeholder="Rule name"
                                               onChange={e => patch(r._key, { name: e.target.value })} />
                                        <select style={inputS} value={r.scope}
                                                onChange={e => patch(r._key, { scope: e.target.value })}>
                                            {BSR_SCOPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                            <Button size="slim" variant="primary" disabled={!r._dirty}
                                                    loading={savingKey === r._key} onClick={() => saveRule(r)}>Save</Button>{' '}
                                            <Button size="slim" tone="critical" onClick={() => deleteRule(r)}>Delete</Button>
                                        </span>
                                    </div>
                                    {r.scope !== 'always' && (
                                        <div style={{ margin: '8px 0 4px 34px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {(r.conditions || []).map((c, ci) => {
                                                const fld = BSR_FIELDS.find(f => f.value === c.field) || BSR_FIELDS[0];
                                                const ops = BSR_OPS[fld.kind] || BSR_OPS.text;
                                                return (
                                                    <div key={ci} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <span style={{ fontSize: 12, color: '#6d7175', width: 34 }}>{ci === 0 ? 'When' : 'and'}</span>
                                                        <select style={inputS} value={c.field}
                                                                onChange={e => {
                                                                    const nf = BSR_FIELDS.find(f => f.value === e.target.value);
                                                                    const nops = BSR_OPS[nf?.kind || 'text'];
                                                                    condPatch(r._key, ci, { field: e.target.value,
                                                                        op: nops.some(o => o.value === c.op) ? c.op : nops[0].value });
                                                                }}>
                                                            {BSR_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                                        </select>
                                                        <select style={inputS} value={c.op}
                                                                onChange={e => condPatch(r._key, ci, { op: e.target.value })}>
                                                            {ops.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                        </select>
                                                        <input style={{ ...inputS, minWidth: 240, flex: '1 1 240px' }}
                                                               value={c.value || ''}
                                                               placeholder={fld.kind === 'num' ? 'e.g. 6' : 'value, another value, …'}
                                                               onChange={e => condPatch(r._key, ci, { value: e.target.value })} />
                                                        <button onClick={() => condRemove(r._key, ci)}
                                                                style={{ border: 0, background: 'none', cursor: 'pointer', color: '#b42318' }}>✕</button>
                                                    </div>
                                                );
                                            })}
                                            <div>
                                                <button onClick={() => condAdd(r._key)}
                                                        style={{ border: 0, background: 'none', cursor: 'pointer', color: '#2c6ecb', fontSize: 12.5, padding: 0 }}>
                                                    + add condition
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginLeft: 34, marginTop: 6 }}>
                                        <span style={{ fontSize: 12, color: '#6d7175' }}>Then:</span>
                                        <select style={inputS} value={r.package_key || ''}
                                                onChange={e => patch(r._key, { package_key: e.target.value })}>
                                            <option value="">package: keep computed</option>
                                            {packages.map(p => <option key={p.key} value={p.key}>📦 {p.name} · {p.dims}</option>)}
                                        </select>
                                        <select style={inputS} value={r._ship_via ?? shipViaValue(r)}
                                                onChange={e => patch(r._key, { _ship_via: e.target.value })}>
                                            {BSR_SHIP_VIA.map(o => <option key={o.value} value={o.value}>🚚 {o.label}</option>)}
                                        </select>
                                        <label style={{ fontSize: 12.5 }}>item lb:{' '}
                                            <input type="number" min="0" step="0.1" style={{ ...inputS, width: 64 }}
                                                   value={r.item_weight_lb ?? ''} placeholder="—"
                                                   onChange={e => patch(r._key, { item_weight_lb: e.target.value })} />
                                        </label>
                                        <input style={{ ...inputS, minWidth: 220, flex: '1 1 220px' }} value={r.notes || ''}
                                               placeholder="Notes" onChange={e => patch(r._key, { notes: e.target.value })} />
                                    </div>
                                </div>
                            ))}
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// ── Packing Portal tab ────────────────────────────────────────────────────────

// Normalize reason: always return {text, archived} regardless of whether stored as string or object
function normalizeReason(r) {
    return typeof r === 'string' ? { text: r, archived: false } : r;
}

function PackingPortalTab() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving]     = useState(false);
    const [saveError, setSaveError] = useState('');
    const [newReason, setNewReason] = useState('');
    const [editIdx, setEditIdx]   = useState(null); // index into full (including archived) list
    const [editVal, setEditVal]   = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [dragIdx, setDragIdx]   = useState(null); // active index being dragged
    const [dragOver, setDragOver] = useState(null); // active index being hovered

    useEffect(() => {
        authenticatedFetch('/api/packing/settings')
            .then(r => r.json())
            .then(d => setSettings({ ...d, qa_fail_reasons: (d.qa_fail_reasons || []).map(normalizeReason) }))
            .catch(() => setSettings({ qa_fail_reasons: [] }));
    }, []);

    const save = (reasons) => {
        setSaving(true);
        setSaveError('');
        authenticatedFetch('/api/packing/settings', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ settings: { ...settings, qa_fail_reasons: reasons } }),
        })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => setSettings(s => ({ ...s, qa_fail_reasons: reasons })))
        .catch(() => setSaveError('Failed to save. Please try again.'))
        .finally(() => setSaving(false));
    };

    const allReasons = (settings?.qa_fail_reasons || []);
    const activeReasons   = allReasons.filter(r => !r.archived);
    const archivedReasons = allReasons.filter(r => r.archived);

    const addReason = () => {
        const trimmed = newReason.trim();
        if (!trimmed) return;
        save([...allReasons, { text: trimmed, archived: false }]);
        setNewReason('');
    };

    const archiveReason = (reasonObj) => {
        save(allReasons.map(r => r === reasonObj ? { ...r, archived: true } : r));
    };

    const restoreReason = (reasonObj) => {
        save(allReasons.map(r => r === reasonObj ? { ...r, archived: false } : r));
    };

    const startEdit = (reasonObj) => {
        setEditIdx(allReasons.indexOf(reasonObj));
        setEditVal(reasonObj.text);
    };

    const saveEdit = () => {
        if (!editVal.trim()) return;
        save(allReasons.map((r, i) => i === editIdx ? { ...r, text: editVal.trim() } : r));
        setEditIdx(null);
    };

    const handleDrop = (toActiveIdx) => {
        if (dragIdx === null || dragIdx === toActiveIdx) return;
        const newActive = [...activeReasons];
        const [moved] = newActive.splice(dragIdx, 1);
        newActive.splice(toActiveIdx, 0, moved);
        let ai = 0;
        save(allReasons.map(r => r.archived ? r : newActive[ai++]));
        setDragIdx(null);
        setDragOver(null);
    };

    if (!settings) return <Text>Loading…</Text>;

    return (
        <div className="settings-section">
            <h3 className="settings-section-title">QA Failure Reasons</h3>
            <p className="settings-section-desc">
                These options appear when a worker marks an item as failing QC.
                Removed reasons are archived and preserved for historical records.
            </p>
            <div className="packing-reasons-list">
                {activeReasons.map((reason, activeIdx) => {
                    const globalIdx = allReasons.indexOf(reason);
                    const isDragging = dragIdx === activeIdx;
                    const isOver    = dragOver === activeIdx && dragIdx !== activeIdx;
                    return (
                        <div
                            key={globalIdx}
                            className={`packing-reason-row${isDragging ? ' packing-reason-row--dragging' : ''}${isOver ? ' packing-reason-row--dragover' : ''}`}
                            draggable={editIdx !== globalIdx}
                            onDragStart={() => setDragIdx(activeIdx)}
                            onDragOver={e => { e.preventDefault(); setDragOver(activeIdx); }}
                            onDragLeave={() => setDragOver(null)}
                            onDrop={() => handleDrop(activeIdx)}
                            onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                        >
                            {editIdx === globalIdx ? (
                                <>
                                    <input
                                        className="packing-reason-input"
                                        value={editVal}
                                        onChange={e => setEditVal(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && saveEdit()}
                                        autoFocus
                                    />
                                    <button className="packing-reason-btn" onClick={saveEdit} disabled={saving}>Save</button>
                                    <button className="packing-reason-btn packing-reason-btn--secondary" onClick={() => setEditIdx(null)}>Cancel</button>
                                </>
                            ) : (
                                <>
                                    <span className="packing-reason-drag-handle" title="Drag to reorder">⠿</span>
                                    <span className="packing-reason-label">{reason.text}</span>
                                    <div className="packing-reason-actions">
                                        <button className="packing-reason-btn packing-reason-btn--secondary" onClick={() => startEdit(reason)} disabled={saving}>Edit</button>
                                        <button className="packing-reason-btn packing-reason-btn--danger" onClick={() => archiveReason(reason)} disabled={saving}>Remove</button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="packing-reason-add">
                <input
                    className="packing-reason-input"
                    placeholder="New reason…"
                    value={newReason}
                    onChange={e => setNewReason(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addReason()}
                />
                <button className="packing-reason-btn" onClick={addReason} disabled={saving || !newReason.trim()}>
                    Add Reason
                </button>
            </div>
            {saveError && <p style={{color: '#dc2626', fontSize: 13, marginTop: 8}}>{saveError}</p>}

            {archivedReasons.length > 0 && (
                <div style={{marginTop: 20}}>
                    <button
                        className="packing-reason-btn packing-reason-btn--secondary"
                        onClick={() => setShowArchived(v => !v)}
                        style={{fontSize: 12}}
                    >
                        {showArchived ? '▾' : '▸'} Archived Reasons ({archivedReasons.length})
                    </button>
                    {showArchived && (
                        <div className="packing-reasons-list" style={{marginTop: 8, opacity: 0.65}}>
                            {archivedReasons.map((reason) => (
                                <div key={allReasons.indexOf(reason)} className="packing-reason-row">
                                    <span className="packing-reason-label" style={{textDecoration: 'line-through'}}>{reason.text}</span>
                                    <div className="packing-reason-actions">
                                        <button className="packing-reason-btn packing-reason-btn--secondary" onClick={() => restoreReason(reason)} disabled={saving}>Restore</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── My Profile tab ────────────────────────────────────────────────────────────

function MyProfileTab() {
    const [profile, setProfile]     = useState(null);
    const [form, setForm]           = useState({ first_name: '', last_name: '', email: '' });
    const [pwCurrent, setPwCurrent] = useState('');
    const [pwNew, setPwNew]         = useState('');
    const [pwConfirm, setPwConfirm] = useState('');
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [msg, setMsg]             = useState(null);
    const [msgError, setMsgError]   = useState(false);

    // Avatar upload + crop state
    const [sourceImg, setSourceImg] = useState(null);         // HTMLImageElement
    const [sourceUrl, setSourceUrl] = useState(null);         // object URL
    const [cropX, setCropX]         = useState(0);
    const [cropY, setCropY]         = useState(0);
    const [cropSize, setCropSize]   = useState(0);
    const [dragging, setDragging]   = useState(false);
    const previewCanvasRef          = useRef(null);
    const imgWrapRef                = useRef(null);

    const MAX_DISPLAY = 360; // max px for display size of source image
    // 2026-09-12 (Eric): the avatar is shown inside a round 28px presence
    // circle on the order page (56px), so the cropper masks a circle and stores
    // a 128 × 128 PNG (crisp on retina) instead of the old 40 × 40 square.
    const AVATAR_PX = 128;
    const [displayScale, setDisplayScale] = useState(1); // image_px → display_px

    const flash = (text, err = false) => { setMsg(text); setMsgError(err); };

    useEffect(() => {
        authenticatedFetch('/api/me')
            .then(r => r.json())
            .then(p => {
                setProfile(p);
                setForm({
                    first_name: p.first_name || '',
                    last_name:  p.last_name  || '',
                    email:      p.email      || '',
                });
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSaveProfile = async () => {
        setSaving(true); setMsg(null);
        try {
            const body = {
                first_name: form.first_name,
                last_name:  form.last_name,
                email:      form.email,
            };
            if (pwNew) {
                if (pwNew !== pwConfirm) {
                    setSaving(false);
                    flash('New passwords do not match.', true);
                    return;
                }
                const pwErr = validatePasswordComplexity(pwNew);
                if (pwErr) {
                    setSaving(false);
                    flash(pwErr, true);
                    return;
                }
                body.current_password = pwCurrent;
                body.new_password     = pwNew;
            }
            const res = await authenticatedFetch('/api/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                flash('Profile saved.');
                setPwCurrent(''); setPwNew(''); setPwConfirm('');
                // Refresh profile
                const me = await authenticatedFetch('/api/me').then(r => r.json());
                setProfile(me);
            } else {
                flash(data.detail || 'Save failed.', true);
            }
        } catch (e) {
            flash('Network error.', true);
        } finally {
            setSaving(false);
        }
    };

    const handleFilePick = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { flash('Please select an image file.', true); return; }
        if (file.size > 1024 * 1024) { flash('Image must be smaller than 1 MB.', true); return; }
        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        const url = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            setSourceImg(img);
            setSourceUrl(url);
            // Initial crop = biggest square that fits, centered
            const minSide = Math.min(img.naturalWidth, img.naturalHeight);
            setCropSize(minSide);
            setCropX((img.naturalWidth  - minSide) / 2);
            setCropY((img.naturalHeight - minSide) / 2);
            // Compute display scale
            const scale = Math.min(1, MAX_DISPLAY / Math.max(img.naturalWidth, img.naturalHeight));
            setDisplayScale(scale);
            setMsg(null);
        };
        img.onerror = () => flash('Could not read image.', true);
        img.src = url;
        e.target.value = ''; // allow re-selecting the same file
    };

    // Redraw 40x40 preview whenever crop params change
    useEffect(() => {
        if (!sourceImg || !previewCanvasRef.current || cropSize <= 0) return;
        const canvas = previewCanvasRef.current;
        canvas.width  = AVATAR_PX;
        canvas.height = AVATAR_PX;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, AVATAR_PX, AVATAR_PX);
        ctx.drawImage(sourceImg, cropX, cropY, cropSize, cropSize, 0, 0, AVATAR_PX, AVATAR_PX);
    }, [sourceImg, cropX, cropY, cropSize]);

    const handleMouseDown = (e) => { setDragging(true); e.preventDefault(); };
    const handleMouseUp   = () => setDragging(false);
    const handleMouseMove = (e) => {
        if (!dragging || !sourceImg) return;
        const rect = imgWrapRef.current.getBoundingClientRect();
        // Mouse position relative to image (in display px), centered on the crop
        const dx = (e.clientX - rect.left) / displayScale - cropSize / 2;
        const dy = (e.clientY - rect.top)  / displayScale - cropSize / 2;
        const maxX = sourceImg.naturalWidth  - cropSize;
        const maxY = sourceImg.naturalHeight - cropSize;
        setCropX(Math.max(0, Math.min(maxX, dx)));
        setCropY(Math.max(0, Math.min(maxY, dy)));
    };

    const changeCropSize = (delta) => {
        if (!sourceImg) return;
        const minSize = Math.min(40, sourceImg.naturalWidth, sourceImg.naturalHeight);
        const maxSize = Math.min(sourceImg.naturalWidth, sourceImg.naturalHeight);
        let next = Math.max(minSize, Math.min(maxSize, cropSize + delta));
        // Clamp position so the crop stays inside the image
        setCropSize(next);
        setCropX(x => Math.max(0, Math.min(sourceImg.naturalWidth  - next, x)));
        setCropY(y => Math.max(0, Math.min(sourceImg.naturalHeight - next, y)));
    };

    const handleSaveAvatar = async () => {
        if (!previewCanvasRef.current) return;
        setSaving(true); setMsg(null);
        try {
            const dataUrl = previewCanvasRef.current.toDataURL('image/png');
            const res = await authenticatedFetch('/api/me/avatar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_data_url: dataUrl }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                flash('Avatar saved.');
                setProfile(p => ({ ...p, avatar_path: data.avatar_path }));
                // Reset the cropper
                if (sourceUrl) URL.revokeObjectURL(sourceUrl);
                setSourceImg(null);
                setSourceUrl(null);
            } else {
                flash(data.detail || 'Avatar upload failed.', true);
            }
        } catch (e) {
            flash('Network error.', true);
        } finally {
            setSaving(false);
        }
    };

    const cancelCrop = () => {
        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        setSourceImg(null);
        setSourceUrl(null);
    };

    if (loading) return <Text>Loading…</Text>;

    const displayW = sourceImg ? sourceImg.naturalWidth  * displayScale : 0;
    const displayH = sourceImg ? sourceImg.naturalHeight * displayScale : 0;
    // Cache-bust the current avatar using updated_at (or profile state)
    const currentAvatarUrl = profile?.avatar_path
        ? `${profile.avatar_path}?t=${Date.now()}`
        : null;

    return (
        <BlockStack gap="400">
            {msg && <Banner tone={msgError ? 'critical' : 'success'} onDismiss={() => setMsg(null)}>{msg}</Banner>}

            {/* ── Profile info ── */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h3">Profile</Text>
                    <Text tone="subdued" as="p">Username: <strong>{profile?.username}</strong> · Role: <strong>{profile?.role}</strong></Text>
                    <InlineStack gap="300">
                        <div style={{ flex: 1 }}>
                            <TextField label="First name" value={form.first_name}
                                onChange={v => setForm(f => ({ ...f, first_name: v }))} autoComplete="given-name" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <TextField label="Last name" value={form.last_name}
                                onChange={v => setForm(f => ({ ...f, last_name: v }))} autoComplete="family-name" />
                        </div>
                    </InlineStack>
                    <TextField label="Email" type="email" value={form.email}
                        onChange={v => setForm(f => ({ ...f, email: v }))} autoComplete="email" />
                </BlockStack>
            </Card>

            {/* ── Change password ── */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h3">Change password</Text>
                    <Text tone="subdued" as="p">Leave blank to keep your current password. {PASSWORD_RULES_HINT}</Text>
                    <TextField label="Current password" type="password" value={pwCurrent}
                        onChange={setPwCurrent} autoComplete="current-password" />
                    <InlineStack gap="300">
                        <div style={{ flex: 1 }}>
                            <TextField label="New password" type="password" value={pwNew}
                                onChange={setPwNew} autoComplete="new-password" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <TextField label="Confirm new password" type="password" value={pwConfirm}
                                onChange={setPwConfirm} autoComplete="new-password" />
                        </div>
                    </InlineStack>
                </BlockStack>
            </Card>

            {/* ── Save profile button ── */}
            <InlineStack gap="200">
                <Button variant="primary" loading={saving} onClick={handleSaveProfile}>Save profile</Button>
            </InlineStack>

            {/* ── Avatar ── */}
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h3">Avatar</Text>
                    <Text tone="subdued" as="p">Upload an image (&lt; 1 MB), then drag to position the circle. Whatever is inside the circle is what other people see next to the order title while you have it open.</Text>

                    <InlineStack gap="400" blockAlign="start">
                        <div>
                            <Text tone="subdued">Current avatar</Text>
                            <div style={{ marginTop: 6 }}>
                                {currentAvatarUrl ? (
                                    <img src={currentAvatarUrl} alt="avatar" width={40} height={40}
                                        style={{ objectFit: 'cover', border: '1px solid #e1e3e5', borderRadius: '50%' }} />
                                ) : (
                                    <div style={{ width: 40, height: 40, background: '#f6f6f7', border: '1px dashed #c9cccf', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#8c9196' }}>
                                        none
                                    </div>
                                )}
                            </div>
                        </div>
                    </InlineStack>

                    {!sourceImg && (
                        <div>
                            <label style={{ display: 'inline-block', padding: '8px 16px', background: '#1a4fa0', color: '#fff', borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>
                                Select image…
                                <input type="file" accept="image/*" onChange={handleFilePick} style={{ display: 'none' }} />
                            </label>
                        </div>
                    )}

                    {sourceImg && (
                        <BlockStack gap="300">
                            <InlineStack gap="400" blockAlign="start">
                                {/* Image + crop overlay */}
                                <div
                                    ref={imgWrapRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    style={{
                                        position: 'relative',
                                        width: displayW,
                                        height: displayH,
                                        userSelect: 'none',
                                        cursor: dragging ? 'grabbing' : 'grab',
                                    }}
                                >
                                    <img
                                        src={sourceUrl}
                                        alt="upload"
                                        draggable={false}
                                        style={{ width: displayW, height: displayH, display: 'block', pointerEvents: 'none' }}
                                    />
                                    {/* Dim overlay with a round hole where the crop circle is */}
                                    {(() => {
                                        const cx = (cropX + cropSize / 2) * displayScale;
                                        const cy = (cropY + cropSize / 2) * displayScale;
                                        const r  = (cropSize / 2) * displayScale;
                                        const mask = `radial-gradient(circle at ${cx}px ${cy}px, transparent ${r}px, #000 ${r + 1}px)`;
                                        return <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', pointerEvents: 'none', WebkitMaskImage: mask, maskImage: mask }} />;
                                    })()}
                                    {/* Crop circle outline */}
                                    <div style={{
                                        position: 'absolute',
                                        left: cropX * displayScale,
                                        top:  cropY * displayScale,
                                        width:  cropSize * displayScale,
                                        height: cropSize * displayScale,
                                        border: '2px solid #ffffff',
                                        borderRadius: '50%',
                                        boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
                                        pointerEvents: 'none',
                                    }} />
                                </div>
                                {/* Live preview */}
                                <div>
                                    <Text tone="subdued">Preview</Text>
                                    <canvas
                                        ref={previewCanvasRef}
                                        width={AVATAR_PX}
                                        height={AVATAR_PX}
                                        style={{ width: 96, height: 96, border: '1px solid #e1e3e5', borderRadius: '50%', marginTop: 6, display: 'block' }}
                                    />
                                    <div style={{ marginTop: 10 }}>
                                        <Text tone="subdued">On the order page:</Text>
                                        <div style={{ marginTop: 4, position: 'relative', width: 64, height: 64 }}>
                                            <canvas
                                                width={AVATAR_PX}
                                                height={AVATAR_PX}
                                                ref={el => {
                                                    if (el && sourceImg) {
                                                        const ctx = el.getContext('2d');
                                                        ctx.clearRect(0, 0, AVATAR_PX, AVATAR_PX);
                                                        ctx.drawImage(sourceImg, cropX, cropY, cropSize, cropSize, 0, 0, AVATAR_PX, AVATAR_PX);
                                                    }
                                                }}
                                                style={{ width: 56, height: 56, borderRadius: '50%', boxShadow: '0 0 0 2px #fff, 0 0 0 4px rgba(0,0,0,0.15)', display: 'block', margin: '4px 0 0 4px' }}
                                            />
                                            {/* Initials badge, same as the order-page presence circle */}
                                            <span style={{ position: 'absolute', right: 0, bottom: 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', color: '#202223', fontSize: 10, fontWeight: 700, lineHeight: '22px', textAlign: 'center', letterSpacing: -0.2, boxShadow: '0 0 0 2px #fff, 0 1px 3px rgba(0,0,0,0.35)' }} aria-hidden>
                                                {(((profile?.first_name || '')[0] || '') + ((profile?.last_name || '')[0] || '') || (profile?.username || '?').slice(0, 2)).toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </InlineStack>

                            <InlineStack gap="200" blockAlign="center">
                                <Button onClick={() => changeCropSize(-Math.max(10, Math.round(cropSize * 0.1)))}>− Smaller circle</Button>
                                <Button onClick={() => changeCropSize(Math.max(10, Math.round(cropSize * 0.1)))}>+ Larger circle</Button>
                                <Text tone="subdued">Circle: {Math.round(cropSize)} px across (image is {sourceImg.naturalWidth} × {sourceImg.naturalHeight})</Text>
                            </InlineStack>

                            <InlineStack gap="200">
                                <Button variant="primary" loading={saving} onClick={handleSaveAvatar}>Save avatar</Button>
                                <Button onClick={cancelCrop}>Cancel</Button>
                            </InlineStack>
                        </BlockStack>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}

// ── Main Settings page ────────────────────────────────────────────────────────

const TAB_IDS = ['profile', 'locations', 'users', 'mention-contacts', 'pressers', 'session', 'team-portal', 'bulk-shipping', 'picking-portal', 'packing-portal', 'cron-monitor', 'lightspeed', 'briefing', 'advanced'];

export default function Settings() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, ['admin']);
    const [searchParams, setSearchParams] = useSearchParams();

    const allTabs = [
        { id: 'profile',   content: 'My Profile',                  panelID: 'profile-panel',   allRoles: true },
        { id: 'locations', content: 'Locations',                   panelID: 'locations-panel', adminOnly: true },
        { id: 'users',     content: 'Operations Hub Users',        panelID: 'users-panel',    managerAccess: true },
        // Mention Contacts (Eric, 2026-10-21): shared Shopify staff accounts offered in comment @mentions.
        // 2026-10-27: renamed Shopify Contacts (it also holds FBM Emails); the tab id stays so old links work.
        { id: 'mention-contacts', content: 'Shopify Contacts', panelID: 'mention-contacts-panel', adminOnly: true },
        { id: 'pressers',  content: 'Portal (PIN) Users', panelID: 'pressers-panel', managerAccess: true },
        { id: 'notifications', content: 'Order Notifications', panelID: 'notifications-panel', managerAccess: true },
        { id: 'sku-tools', content: 'SKU Tools', panelID: 'sku-tools-panel', managerAccess: true },
        { id: 'session',   content: 'Shopify Session',             panelID: 'session-panel',  adminOnly: true },
        { id: 'team-portal', content: 'Team Portal',                 panelID: 'team-portal-panel', adminOnly: true },
        // Bulk Shipping Rules rides the bulk-labels TEST flag — hidden on prod
        // until the feature launches (2026-09-01 user spec).
        ...(window.location.hostname.startsWith('operations-test')
            ? [{ id: 'bulk-shipping', content: 'Bulk Shipping Rules', panelID: 'bulk-shipping-panel', adminOnly: true }] : []),
        { id: 'picking-portal', content: 'Picking Portal',           panelID: 'picking-portal-panel', adminOnly: true },
        { id: 'packing-portal', content: 'Packing Portal',         panelID: 'packing-portal-panel', adminOnly: true },
        { id: 'cron-monitor', content: 'Cron Monitor', panelID: 'cron-monitor-panel', adminOnly: true },
        { id: 'lightspeed', content: 'Lightspeed', panelID: 'lightspeed-panel', adminOnly: true },
        // Briefing (Eric, 2026-10-05): Team Notes, run days, order day, goals sheet and the (read-only) prompt.
        { id: 'briefing', content: 'Briefing', panelID: 'briefing-panel', adminOnly: true, permission: 'settings.store_briefing' },
        { id: 'advanced',  content: 'Advanced',                    panelID: 'advanced-panel', adminOnly: true },
    ];
    const isManager = hasRole(user, ['franchise', 'site_manager']);
    // Shopify Session is admin + site_manager (NOT franchise) — site managers
    // need it to download/install the Chrome extension. The HAR-upload card
    // inside the tab is still gated to admin (see ShopifySessionTab).
    const sessionAllowed = isAdmin || hasRole(user, ['site_manager']);
    // Notifications has its own role list — admin / site_manager /
    // customer_service / franchise / user. It overrides the more restrictive
    // CRM / customer_service / user gating below.
    const NOTIFICATION_TAB_ROLES = ['admin', 'site_manager', 'customer_service', 'franchise', 'user'];
    const tabs = allTabs.filter(t => {
        // Extra permissions (Eric, 2026-10-08): an exact grant opens the tab
        // for anyone, ahead of every role rule below (admins hold them all).
        if (t.permission && hasPermission(user, t.permission)) return true;
        if (t.id === 'notifications') {
            return hasRole(user, NOTIFICATION_TAB_ROLES);
        }
        // store_manager sees only Profile + Portal (PIN) Users.
        if (hasRole(user, ['store_manager'])) return t.id === 'profile' || t.id === 'pressers';
        // CRM and customer_service are otherwise read-only Settings users —
        // only their own profile is visible. (customer_service has elevated
        // operational rights elsewhere — print, change pickup, all-loc
        // scope — but no other Settings management.)
        if (hasRole(user, ['crm', 'customer_service', 'finance', 'external'])) return t.id === 'profile';
        if (t.id === 'session') return sessionAllowed;
        if (isAdmin) return true;
        if (t.adminOnly) return false;
        // SKU Tools also opens to role `user` (2026-09-11, Eric) — the API
        // already scopes their broadcasts to their own locations.
        if (t.id === 'sku-tools') return isManager || hasRole(user, ['user']);
        if (t.managerAccess) return isManager;
        return true;
    });
    const tabIds = tabs.map(t => t.id);

    const [selected, setSelected] = useState(() => {
        const idx = tabIds.indexOf(searchParams.get('tab'));
        return idx >= 0 ? idx : 0;
    });

    const handleSelect = (idx) => {
        setSelected(idx);
        setSearchParams({ tab: tabIds[idx] }, { replace: true });
    };

    const currentTabId = tabIds[selected];

    const primaryTabs = tabs.map(t => ({ id: t.id, content: t.content }));
    const handlePrimarySelect = (id) => {
        const idx = tabIds.indexOf(id);
        if (idx >= 0) {
            setSelected(idx);
            setSearchParams({ tab: id }, { replace: true });
        }
    };

    return (
        <div style={{ maxWidth: '98vw' }}>
        <Page title="Settings" fullWidth>
            <FolderTabsPanel
                tabs={primaryTabs}
                selectedId={currentTabId}
                onSelect={handlePrimarySelect}
                variant="primary"
            >
                {currentTabId === 'profile' && <MyProfileTab />}
                {currentTabId === 'locations' && <LocationsTab />}
                {currentTabId === 'users' && <UsersTab />}
                {currentTabId === 'mention-contacts' && <MentionContactsTab />}
                {currentTabId === 'pressers' && <PresserUsersTab />}
                {currentTabId === 'notifications' && <NotificationsTab />}
                {currentTabId === 'sku-tools' && <SkuToolsTab />}
                {currentTabId === 'session' && <ShopifySessionTab />}
                {currentTabId === 'team-portal' && <TeamPortalTab />}
                {currentTabId === 'bulk-shipping' && <BulkShippingRulesTab />}
                {currentTabId === 'picking-portal' && <PickingPortalTab />}
                {currentTabId === 'packing-portal' && <PackingPortalTab />}
                {currentTabId === 'cron-monitor' && <CronMonitorTab />}
                {currentTabId === 'lightspeed' && <LightspeedTab />}
                {currentTabId === 'briefing' && <StoreBriefingPromptCard />}
                {currentTabId === 'advanced' && <AdvancedTab />}
            </FolderTabsPanel>
        </Page>
        </div>
    );
}
